import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SideBar, { RECENTS_REFRESH_EVENT, RECENTS_OPTIMISTIC_EVENT, RECENTS_CLOSED_EVENT } from "../SideBar";
import FeedbackModal from "../FeedbackModal";
import UserProfileMenu from "../UserProfileMenu";
import CustomerChatPanel from "./CustomerChatPanel";
import { stripCitations } from "./utils/formatMessage";
import CopilotPanel from "./CopilotPanel";
import ZoomLinkModal from "./ZoomLinkModal";
import LearningMode from "../learning/LearningMode";
import GlobalContextPanel from "./GlobalContextPanel";
import { splitCloseIntent } from "./utils/closeIntent";
import { FOLLOWUPS_MARKER } from "./QuestionCard";
import { uploadAttachments } from "./utils/attachments";
import useTseSocket from "./hooks/useTseSocket";
import useSidebar from "./hooks/useSidebar";
import { slugifySourceKey, sourceMapKey, buildSourceMapUrl } from "../tse/sourceMapLink";
import { authFetch } from "../../utils/auth";
import BrainPopover from "../brain/BrainPopover";
import { diagnosisOpen } from "../brain/popoverLogic";
import { promptCommand } from "../brain/brainCommands";
import { PROMPT_STATUS } from "../brain/DiagnosisPromptBanner";
import { buildAnswer, normalizeQuestion } from "../brain/questionAnswer";
import { buildBatchAnswer, DA_ANSWER_BLOCK, DA_QUESTION_BLOCK, isBatchAnswer, isBatchQuestion, replayDaAnswer } from "./utils/batchAnswer";
import useBrainState from "../brain/useBrainState";
import { DA_NARRATION_BLOCK, replayNarrationMessage } from "../brain/daNarrationLogic";
import useDaNarration from "./hooks/useDaNarration";
import useTurnSendGuard from "./hooks/useTurnSendGuard";
import useInFlightRejoin from "./hooks/useInFlightRejoin";
import useCommandBusy from "./hooks/useCommandBusy";
import useSessionLearning, { LEARNING_TOGGLE } from "./hooks/useSessionLearning";
import { NEW_SESSION_PATH } from "./sessionRoutes";
import { absorbCostPayload, bankTurnCost, createCostLedger, seedCostLedger } from "./utils/turnCost";
import "./styles/dashboard5.css";

// The diagnosis popover's stored-data browser is for dev / SME users only (16.4); the server enforces
// it (403), this only hides the section. Read directly (auth.getStoredRole is the same key) and guarded.
const BRAIN_DEV_ROLE = "admin";
const readStoredRole = () => {
  try {
    return window.localStorage.getItem("role") || "";
  } catch (_) {
    return "";
  }
};

const RAG_SEARCH_WINDOW = "width=1200,height=800,scrollbars=yes,resizable=yes";

// How long a dropped socket may stay "reconnecting" before the session is declared
// lost. MUST exceed socket.js's own `timeout: 120000`, or this timer guillotines a
// session while a single connect attempt is still legitimately in progress — the
// exact failure it was written to prevent. Laptop sleep makes that worse: setTimeout
// fires the moment the machine wakes, racing the reconnect it is meant to outlast.
const RECONNECT_GRACE_MS = 150000;
// Once the transport is back the join ack should be immediate, so a much shorter
// window applies from that point. Stops a server that accepts connections but never
// answers the join from holding the composer for the full grace period.
const JOIN_ACK_GRACE_MS = 20000;
// A retryable access denial (the socket has no usable identity) is answered by
// refreshing the token and reconnecting. Bounded, because a server that keeps
// refusing would otherwise turn into a reconnect loop hammering both the auth
// endpoint and the socket.
const AUTH_RETRY_LIMIT = 2;
const AUTH_RETRY_WINDOW_MS = 30000;
// How long to wait for the server to mint a session id (tse:start ->
// tse:session_created) before giving the message back to the engineer. Short:
// the server does no I/O to answer it, so anything past a couple of seconds is
// a dead socket rather than a slow one.
const START_SESSION_TIMEOUT_MS = 10000;
// How long the "Loading conversation…" state may stand before it gives up and
// offers a retry. Generous on purpose: a session stored before the envelope fix
// can take 10-20s to replay, so anything tighter would abort a load that was
// about to succeed. Its only job is to guarantee the skeleton is never permanent.
const HYDRATE_GRACE_MS = 30000;
// Safety valve for the close-and-continue clear gate (clearInFlightRef). A
// clear normally answers in well under a second; this only exists so a clear
// the server never acknowledges cannot leave replay disabled for the rest of
// the mount. Long enough to cover a slow round trip, short enough that a lost
// tse:cleared costs one refresh rather than a silently broken tab.
const CLEAR_INFLIGHT_GRACE_MS = 8000;

// Actions that run a full agent turn. The label is echoed into the transcript
// so the user sees what they asked for while the agent works — these take
// several seconds and would otherwise look like nothing happened.
const ACTION_ECHO = {
  find_bugs: "Find related bugs for this issue.",
  find_cases: "Find related support cases for this issue.",
  active_incident: "Check for active incidents related to this issue.",
  next_step: "What's the next step?",
  possible_causes: "What are the possible causes?",
};

// Sent on the engineer's behalf when Root Cause Analysis mode is on and a
// batch of call context lands. Deliberately does NOT restate the context: the
// server attaches it to the session and prefixes it onto the turn, so quoting
// it here would hand the model the same facts twice.
const RCA_PROMPT = "Do a root cause analysis based on the call context.";
// Teaching a cause that is already in the graph learns it and then closes the session like the close intent does: this is
// the wrap-up the close shows.
const TEACH_CLOSE_SUMMARY = "The cause was taught and the diagnosis is closed.";
const TEACH_COMMAND = "teach";

export const genSessionId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `sess-${Date.now()}-${Math.random().toString(16).slice(2)}`;

// A RELOAD IS A NEW SESSION — UNLESS A WALKTHROUGH IS OPEN.
//
// The URL is the source of truth: /TseDashboard5/<session-id>. A session is a
// resource, so it gets an address — which is what lets history link to one, and
// what stops the context panel showing a session the page is no longer on.
//
// A session that has not been started yet is the one thing that has no id worth
// showing. Nothing exists server-side until the first turn commits, so the old
// scheme — mint a uuid on "New Copilot Session" and redirect straight to it — put
// an address in the bar for a conversation the server had never heard of. It
// reads as "this chat exists": it is in the URL, it looks shareable, and it is
// absent from Recents. So a blank chat gets its own, honest address instead:
//
//   /TseDashboard5/new   a blank session, nothing stored, nothing to share
//   /TseDashboard5/<id>  a real session (shared link, reload, Recents)
//   /TseDashboard5       redirects to /new (see App.js)
//
// And the id itself comes from the SERVER, not the browser. /new holds no id at
// all; the first thing the engineer does sends tse:start, the server mints one,
// and tse:session_created is what puts it in the ref and in the address bar
// (see onSessionCreated). So an id in the URL always means a session this
// server created — which is the whole reason the browser stopped naming them.
//
// The remount that this obviously risks is handled in the route wrapper, not
// here: App.js keys the dashboard on a value it holds across the /new -> /<id>
// transition. Keying on the raw path segment would flip the key mid-turn,
// unmount the subtree and take `messages` — including the user bubble that was
// just appended — with it, while the socket dropped in the middle of the very
// first answer.
//
// The id is NOT read back from sessionStorage. sessionStorage is not per-tab the
// way it reads: the HTML spec has the storage area COPIED into any browsing
// context opened from this one, so Cmd/Ctrl+click, target=_blank and "duplicate
// tab" all start with the opener's id. Reading it on a bare route made every
// such tab rejoin the opener's session — the server log showed three separate
// sockets joining one session_id inside 22ms — and since events are addressed to
// session.sid, the newest join silently steals every answer from the older tabs
// while the context panel shows them the wrong conversation.
//
// So: the URL names the session, or says plainly that there is not one yet.
//
// Kept for embedded mode only, which has no route of its own to carry an id.
const SESSION_ID_KEY = "d5_session_id";
const SESSION_MODE_KEY = "d5_session_mode";

const rememberSessionMode = (mode) => {
  try {
    sessionStorage.setItem(SESSION_MODE_KEY, mode === "guided" ? "guided" : "fast");
  } catch { /* private mode — resume simply stops working, which is the safe way to fail */ }
};

// `fresh` is the enterprise "New chat": the rail navigated to /ent-user with no
// id, which means start a session nobody has seen, not resume whatever is in
// sessionStorage. Without it a guided chat would be handed straight back and
// "New chat" would reopen the chat it was supposed to leave.
const loadEmbeddedSessionId = (fresh = false) => {
  try {
    if (!fresh && sessionStorage.getItem(SESSION_MODE_KEY) === "guided") {
      const stored = sessionStorage.getItem(SESSION_ID_KEY);
      if (stored) return stored;
    }
  } catch {
    // Private mode / storage disabled — a fresh id per mount, which is exactly
    // what a hybrid session wants anyway.
  }
  const id = genSessionId();
  try {
    sessionStorage.setItem(SESSION_ID_KEY, id);
    sessionStorage.setItem(SESSION_MODE_KEY, "fast");
  } catch { /* not fatal */ }
  return id;
};

// Rebuild call-context blocks from the server's joined string. The blocks are
// joined with a blank line and each starts with a "[HH:MM:SS]" timestamp line
// (stamped by handleRequestDiagnosis). Text with no stamp comes back as one
// block, which is fine: split-then-join is lossless either way.
const parseCallContextBlocks = (joined) => {
  const text = String(joined || "");
  if (!text.trim()) return [];
  return text
    .split(/\n\n(?=\[\d{1,2}:\d{2}:\d{2}\]\n)/)
    .filter((t) => t.trim())
    .map((t, i) => ({ id: `ctx-resume-${i}`, text: t }));
};

// One transcript card per block, in the same shape handleRequestDiagnosis
// appends live, so a replayed card and the one the engineer watched appear
// render identically.
const callContextCards = (blocks) => (blocks || []).map((b) => ({
  id: b.id,
  type: "call_context",
  text: b.text,
  timestamp: b.timestamp || Date.now(),
}));

const TseDashboard5 = ({
  onLogout,
  embedded = false,
  // The id this dashboard is bound to, resolved by the route wrapper. On
  // /new that is a freshly minted id the address bar does not show yet; on
  // /TseDashboard5/<id> it is the id in the path. Passed down rather than read
  // from useParams so both cases arrive the same way.
  sessionId: sessionIdProp,
  // True on /new: there is no session at all yet — no id, nothing stored, and
  // the address bar is still showing the blank-session path.
  isNewSession = false,
  // Told the id the server minted for this mount, so the route wrapper knows
  // the address is about to become one it is already showing and must not
  // remount on it. See App.js: TseDashboard5Route.
  onSessionIdAssigned,
  // A ref the PARENT owns. This dashboard writes its own send function into
  // it, so a wrapper can put a message into this chat by calling exactly what
  // the composer calls. See the "ghost" note below.
  sendRef = null,
  variant = "default",
  agentNameOverride = "",
  sentiment: sentimentProp,
  prevSentiment: prevSentimentProp,
  onSentimentUpdate: onSentimentUpdateProp,
  tseAgentName = "",
  username: loginUsername = "",
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── The escalated customer this dashboard is attending to ─────────────
  // A SECOND session id, distinct from this dashboard's own. The engineer used
  // to arrive on /TseDashboard5/<customerSessionId>, which bound the whole
  // dashboard — copilot included — to the customer's session and socket room:
  // the copilot's prompts and answers WERE the customer's prompts and answers.
  // Now the customer's id rides in the query string and addresses one thing
  // only, the live chat on the left. The copilot on the right mints its own
  // session like any other chat.
  //
  // Read once per mount, from the search string rather than into state: it is
  // a property of how this tab was opened, and re-resolving it mid-session
  // would re-point the live chat at a different customer.
  const customerSessionIdRef = useRef((() => {
    if (embedded) return null;
    const raw = (new URLSearchParams(location.search).get("customer") || "").trim();
    // A missing param stringifies to "undefined" on the way into a URL, and
    // that string was then sent to the server as a session id —
    // `tse:load_chat_history requested='undefined' -> nothing to show` — while
    // the pane sat on "Loading this customer's conversation…" for ever. Treat
    // the placeholder spellings as no customer at all.
    if (!raw || raw === "undefined" || raw === "null") return null;
    return raw;
  })());
  // The socket id the live-agent seat was last claimed for. Keyed on the id
  // rather than a plain "done" flag because a reconnect issues a NEW socket id
  // and the server holds the seat by sid — so it has to be re-claimed, or the
  // customer's messages are relayed to a socket that no longer exists.
  const boundSocketIdRef = useRef(null);

  const [localSentiment, setLocalSentiment] = useState(null);
  const [localPrevSentiment, setLocalPrevSentiment] = useState(null);

  const sentiment = sentimentProp !== undefined ? sentimentProp : localSentiment;
  const prevSentiment = prevSentimentProp !== undefined ? prevSentimentProp : localPrevSentiment;

  const onSentimentUpdate = useCallback((data) => {
    // While attending an escalation this dashboard is on TWO sessions: its own
    // copilot and the customer's. The gauge is about the customer, so an
    // update carrying any other session's id is dropped rather than allowed to
    // overwrite it. Payloads without a session_id predate the field and are
    // let through, as they can only be this session's own.
    const from = data?.session_id ? String(data.session_id) : "";
    const attending = customerSessionIdRef.current;
    if (attending && from && from !== attending) return;
    if (data && typeof data.sentiment === "number") {
      if (onSentimentUpdateProp) {
        onSentimentUpdateProp(data);
      } else {
        setLocalSentiment((current) => {
          setLocalPrevSentiment(current);
          return data.sentiment;
        });
      }
    }
  }, [onSentimentUpdateProp]);
  const agentName = localStorage.getItem("username") || "TSE Agent";
  // Resolved ONCE per mount. A ref, not state, because every consumer reads it
  // imperatively (socket join, fetches) and re-resolving mid-session would swap
  // the id out from under a live socket.
  //
  // Embedded mode has no router path of its own, so it keeps the stored id.
  // EMPTY on a blank chat, and that is the point: the browser does not name
  // sessions any more. On /TseDashboard5/<id> the id comes off the route; on
  // /new there is nothing until the server mints one (tse:start ->
  // tse:session_created) and onSessionCreated below fills this in.
  //
  // Not read from useParams() either: on /new the path segment is the literal
  // string "new", and binding a socket to a session called "new" would put
  // every engineer who ever opened a blank chat in the same conversation.
  //
  // Embedded mode is the one exception — it has no route to carry an id, so it
  // keeps the sessionStorage one.
  //
  // Embedded now reads `sessionIdProp` FIRST. Enterprise reopens a saved chat
  // by navigating to /ent-user/<id>, and ChatWindow passes that id down here —
  // before this line existed the embedded branch ignored the prop entirely and
  // minted its own id, so a row clicked in the rail opened an empty session
  // under the clicked session's address. With no prop it falls back to the
  // stored id as it always has (and to a fresh one on "New chat").
  // A NEW embedded chat is named by the SERVER, exactly like a routed one.
  // Minting it here instead meant the first tse:join carried an id the server
  // had never heard of, and a join for an unknown id is refused — which reached
  // the customer as "Session timed out" for half a second before the real
  // session was created behind it. The blank-chat case therefore starts with no
  // id at all and waits for tse:session_created (onSessionCreated below, which
  // announces the id to the owner before anything else).
  //
  // The sessionStorage fallback stays for an embedded caller that does NOT
  // declare itself new — it has no route to carry an id, so that is still the
  // only thing that survives its remount.
  const sessionIdRef = useRef(
    embedded
      ? (sessionIdProp || (isNewSession ? null : loadEmbeddedSessionId()))
      : (isNewSession ? null : (sessionIdProp || null))
  );
  // A RENDER-VISIBLE MIRROR OF THE REF ABOVE. The ref stays authoritative and
  // stays synchronous — every emit path still reads sessionIdRef.current in the
  // same tick it was set, which is what keeps the mint invisible and the
  // successor session addressable before the re-render lands. Do not reorder
  // that; this only follows.
  //
  // It exists because a session is now named by the SERVER, so on a new chat
  // the ref starts null and is filled a turn later. A ref filled later cannot
  // reach a child: mutating it triggers no re-render, so CustomerChatPanel kept
  // the empty string it was handed at mount, bound the Zoom bridge with
  // session_id="" and never received a transcript — the left pane sat on
  // "Waiting for call to connect" for the whole call. Every assignment to the
  // ref sets this too, and the panel reads this.
  //
  // ONLY CustomerChatPanel reads it. CopilotPanel and FeedbackModal keep
  // reading the ref: both re-render on their own state whenever anything
  // happens, so their read is already fresh, and moving them onto a value that
  // lags the ref by one render would change session handling for no fault. The
  // Zoom bridge is the one that cannot recover — it BINDS ONCE with whatever it
  // was handed at that moment, and never re-renders on an id nobody told it
  // about.
  const [sessionId, setSessionId] = useState(sessionIdRef.current);

  // Whether this mount has a sidebar with a Recents list behind it.
  //
  // `!embedded` used to be the whole test, because embedded meant "no rail".
  // Enterprise is embedded AND has one now (ChatWindow renders SideBar
  // mode="user"), so the freshness events — the optimistic row on the first
  // turn, the Closed mark on a retired session — have to reach it too, or an
  // enterprise rail lags a full outbox flush behind and never says "Closed".
  //
  // Scoped to `variant === "enterprise"` rather than to `embedded`: enterprise
  // is the only embedded consumer today (ChatWindow is the sole `embedded`
  // call site), and naming the variant keeps a future embedded-without-rail
  // caller working the way this file already promises it does.
  //
  // NOT used for the navigate() guards below. Those stay on `!embedded`: the
  // enterprise address is /ent-user/<id> and it is ChatWindow, not this
  // component, that owns it (see onSessionIdAssigned).
  const hasRecentsRail = !embedded || variant === "enterprise";
  // LEARNING: in-session overlay — open/closed.
  const [learningOpen, setLearningOpen] = useState(false);


  const [leftPct, setLeftPct] = useState(32);
  // Session context (a tab in the results drawer). `ctxTurnKey` ticks when a
  // turn ends, which is the panel's cue to refetch — memory is written after
  // the answer streams, so nothing in the turn's own events carries it.
  const [ctxTurnKey, setCtxTurnKey] = useState(0);
  // Ticks on "New chat". Separate from ctxTurnKey because a clear is not a
  // turn: the panel must drop what it holds immediately, where a turn waits
  // for the extraction to be written.
  const [ctxResetKey, setCtxResetKey] = useState(0);
  const [lastLeftPct, setLastLeftPct] = useState(32);
  const splitContainerRef = useRef(null);
  const isResizingRef = useRef(false);

  const [messages, setMessages] = useState([]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  // Server-driven (tse:product_locked): once the session is past its first
  // prompt the "Product identified" banner can no longer be changed.
  const [productLocked, setProductLocked] = useState(false);
  // From tse:joined: whether the replayed product banner may still be changed,
  // and the products to offer. Read by onReplay, which arrives after the ack.
  const productChangeRef = useRef(null);
  // The product a tse:change_product is waiting on, or null. Set on the click,
  // cleared ONLY by the server's answer (accepted / refused / locked) or by
  // anything that makes the request moot (error, rejoin, clear). While set the
  // banner control is disabled and NOTHING has been wiped — the wipe belongs
  // to tse:product_change_accepted. The ref is the guard (two picks inside one
  // render would both read a stale state), the state is what renders.
  const [productChangePending, setProductChangePending] = useState(null);
  const productChangePendingRef = useRef(null);
  const setChangePending = useCallback((product) => {
    productChangePendingRef.current = product || null;
    setProductChangePending(product || null);
  }, []);
  const [escalationActive, setEscalationActive] = useState(false);
  const escalationActiveRef = useRef(false);
  const [liveChatEnded, setLiveChatEnded] = useState(false);
  const liveChatEndedRef = useRef(false);
  // "agent" | "customer" | null — picks the wording of the transcript status
  // line, which outlives the 8-second notice and is the only place the engineer
  // can still read who closed the chat a minute later.
  const [liveChatEndedBy, setLiveChatEndedBy] = useState(null);
  const [agentJoinedNotice, setAgentJoinedNotice] = useState("");
  const [customerUsername, setCustomerUsername] = useState(() => {
    if (variant === "enterprise") {
      return (loginUsername || localStorage.getItem("username") || "").trim() || "Customer";
    }
    return "Customer";
  });
  const [customerAiAgentName, setCustomerAiAgentName] = useState("");
  const [aiAgentMenuOpen, setAiAgentMenuOpen] = useState(false);
  const aiAgentMenuRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (aiAgentMenuRef.current && !aiAgentMenuRef.current.contains(e.target)) {
        setAiAgentMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const updateEscalationActive = (val) => {
    setEscalationActive(val);
    escalationActiveRef.current = val;
  };

  // Resolved from tseAgentJoined / liveAgentMessage — the static prop from
  // ChatWindow only knows what was in localStorage at mount time.
  const [liveAgentName, setLiveAgentName] = useState(
    () => (tseAgentName || "").trim() || localStorage.getItem("TseAgentName") || ""
  );
  const recordLiveAgentName = useCallback((name) => {
    const trimmed = (name || "").trim();
    const lower = trimmed.toLowerCase();
    if (!trimmed || ["unknown", "user", "agent", "tse agent"].includes(lower)) return;
    setLiveAgentName(trimmed);
    try {
      localStorage.setItem("TseAgentName", trimmed);
    } catch {
      // Quota or private browsing — the in-memory value is enough for this tab.
    }
  }, []);

  useEffect(() => {
    if (tseAgentName) recordLiveAgentName(tseAgentName);
  }, [tseAgentName, recordLiveAgentName]);

  // The id used to be kept out of the URL until the first message landed, on the
  // grounds that a session with no turns is not worth an address. That cost more
  // than it saved: App.js keys this component on the route's session id, so the
  // mid-life navigate flipped the key from "new" to <id> and React unmounted the
  // whole subtree — taking `messages` (including the user bubble that had just
  // been appended) with it, and making useTseSocket's cleanup disconnect the
  // socket in the middle of the very first turn. Anything the server emitted in
  // that gap went to a socket id nobody was listening on.
  //
  // So the id is never minted here. A blank chat sits on /new with no id at all
  // until the server hands one over (onSessionCreated), and the route wrapper
  // holds the React key steady across that swap — so the address changes
  // mid-flight and the tree, the socket and the transcript all stay put.

  // ── Opening a stored session ──────────────────────────────────────────
  // A session named in the URL has a transcript the server has to fetch and
  // replay; for sessions stored before the envelope fix that round trip is
  // still 10-20s. Until it lands there are no messages, and the panel used to
  // draw the "Ready to diagnose" welcome block for the whole wait — which reads
  // as an empty or broken session.
  //
  // Every route carries a session id, so the id alone cannot say whether there
  // is anything to wait for — /new is the distinction. Nothing was ever stored
  // under a /new id, so the welcome block is exactly right there and a skeleton
  // would be a lie. Embedded mode has no route and carries its id in
  // sessionStorage, so it is treated as a new chat rather than showing a
  // skeleton for a transcript that may not exist.
  const isFreshSession = isNewSession;
  // ── Access denied ─────────────────────────────────────────────────────
  // Read with a lazy initializer, NOT in an effect: the effect below wipes
  // location.state on mount, so by the time an effect ran the flag would
  // already be gone. The redirect that sets it happens in the PREVIOUS
  // session's component (see onAccessDenied), which is unmounted by the time
  // this toast is drawn — carrying it through the history entry is the only
  // thing that survives that remount.
  const [accessDeniedToast, setAccessDeniedToast] = useState(
    () => !!location.state?.accessDenied,
  );
  const accessDeniedTimerRef = useRef(null);
  useEffect(() => {
    if (!accessDeniedToast) return undefined;
    accessDeniedTimerRef.current = window.setTimeout(
      () => setAccessDeniedToast(false), 5000,
    );
    return () => {
      if (accessDeniedTimerRef.current) {
        window.clearTimeout(accessDeniedTimerRef.current);
        accessDeniedTimerRef.current = null;
      }
    };
  }, [accessDeniedToast]);
  // Embedded used to be excluded outright, because an embedded id came from
  // sessionStorage and nothing was ever stored under it — a skeleton there
  // would have been a lie. An enterprise chat REOPENED from the rail arrives
  // with `sessionIdProp` set, and that id does name a transcript on the server,
  // so it gets the same skeleton the copilot does rather than painting the
  // "Ready to diagnose" welcome over a conversation that is still in flight.
  const [hydrating, setHydrating] = useState(
    () => (!embedded || !!sessionIdProp) && !isFreshSession
  );
  // The access-denied marker lives in the history entry, so a RELOAD of this URL
  // would hand it back and re-toast a refusal that happened once, minutes ago.
  // Consume it. Same path and same params, so React Router re-renders without
  // remounting and every state seeded from it keeps its value.
  useEffect(() => {
    if (embedded || !location.state) return;
    navigate(location.pathname, { replace: true, state: null });
  }, [embedded, location.pathname, location.state, navigate]);
  // An embedded session is named at MOUNT (sessionStorage, or the prop), not a
  // turn later by the server the way a routed one is — so the one moment the
  // owner can be told which session it is showing is here. ChatWindow uses it
  // to put the id in the address bar (/ent-user/<id>) so a reload comes back to
  // this chat instead of minting another one, and to recognise that address as
  // its own rather than remounting the tree onto it mid-turn.
  //
  // Runs once: the id is seeded into a ref at mount and only ever changes
  // through onCleared / onSessionCreated, which announce it themselves.
  const announceMountedSessionRef = useRef(false);
  useEffect(() => {
    if (!embedded || announceMountedSessionRef.current) return;
    if (!sessionIdRef.current) return;
    announceMountedSessionRef.current = true;
    onSessionIdAssigned?.(sessionIdRef.current);
  }, [embedded, onSessionIdAssigned]);
  // From tse:joined, so the loader can name the conversation being opened.
  const [hydratingTitle, setHydratingTitle] = useState("");
  // The join never answered within HYDRATE_GRACE_MS. Retriable, not terminal.
  const [hydrateFailed, setHydrateFailed] = useState(false);
  const [sessionSource, setSessionSource] = useState(null);
  // Called from EVERY event that proves the transcript is here or is never
  // coming — replay, idle, question, the first chunk, and a join ack reporting
  // zero turns. Deliberately over-subscribed: any one of them missing must not
  // be able to strand a permanent skeleton.
  const endHydrate = useCallback(() => setHydrating(false), []);
  // The last resort behind that: a dropped socket emits nothing at all, so
  // without a deadline the skeleton would sit there for good. Re-arms whenever
  // hydrating flips back on (the retry path), and its cleanup covers unmount.
  useEffect(() => {
    if (!hydrating) return undefined;
    const timer = setTimeout(() => {
      setHydrating(false);
      setHydrateFailed(true);
    }, HYDRATE_GRACE_MS);
    return () => clearTimeout(timer);
  }, [hydrating]);

  const [currentQuestion, setCurrentQuestion] = useState(null);
  // Read by onSourceReplacementStarted, which snapshots the open card before it
  // clears it. A render-synced mirror is enough there: that event arrives a
  // whole user click after the card went up, never in the same dispatch.
  const currentQuestionRef = useRef(null);
  currentQuestionRef.current = currentQuestion;
  const [isStreaming, setIsStreaming] = useState(false);
  // Mirror for reading inside callbacks that close before the next render.
  const isStreamingRef = useRef(false);
  useEffect(() => { isStreamingRef.current = isStreaming; }, [isStreaming]);
  // "fast" | "guided" — from tse:mode. Guided shows Exit guided above composer.
  const [copilotMode, setCopilotMode] = useState("fast");
  // Mirrored into a ref so onDone can read the mode without taking it as a
  // dependency — that callback is rebuilt on every change it depends on, and
  // the mode flips mid-session.
  const copilotModeRef = useRef("fast");
  useEffect(() => { copilotModeRef.current = copilotMode; }, [copilotMode]);
  // Server-side idle timeout. The session is gone and the server refuses to
  // silently mint a replacement under this id, so everything that would talk to
  // it is locked until the user explicitly starts a new session.
  const [sessionExpired, setSessionExpired] = useState(false);
  // The diagnostic session was closed (server sent the feedback prompt). The id
  // is still live — the server revives a closed session on the next message —
  // but the conversation is over, so the composer locks and the post-close
  // banner is the way on. Distinct from sessionExpired, which is terminal.
  const [sessionClosed, setSessionClosed] = useState(false);
  // Socket is down and we are waiting to rejoin. Distinct from sessionExpired: the
  // session is presumed ALIVE until the server says otherwise, so this state is
  // recoverable and the transcript stays usable.
  const [reconnecting, setReconnecting] = useState(false);
  const reconnectTimerRef = useRef(null);
  // Wall-clock stamp of the first drop in the current outage, so repeated
  // connect/disconnect cycles cannot keep extending the grace window.
  const disconnectedSinceRef = useRef(null);
  // Why the session ended, so the banner and composer can say "connection
  // closed" rather than the timeout wording for a dropped socket.
  const [sessionEndReason, setSessionEndReason] = useState(null);
  // Mirror of the above for the socket callbacks. onJoined has to know WHY the
  // session is locked in the same tick the ack lands — a lock set moments
  // earlier has not re-rendered yet, so the state variable it closed over
  // still reads null. Written by `endSession` below, never directly.
  const sessionEndReasonRef = useRef(null);
  // Single writer for the pair, so the ref can never drift from the state.
  const endSession = useCallback((reason) => {
    sessionEndReasonRef.current = reason;
    setSessionEndReason(reason);
  }, []);
  // Set once a session is finished for good (timeout or lost connection).
  // Gates the socket's auto-rejoin: rejoining a dead id mints a BLANK server
  // session behind the same transcript. A ref, not state, because the socket
  // hook reads it from a callback that must see the current value immediately.
  const sessionLockedRef = useRef(false);
  // A disconnect only matters once there is a conversation worth losing —
  // dropping before the first turn just means "start typing", not "your
  // session is gone".
  const hasTurnsRef = useRef(false);
  const [sessionStats, setSessionStats] = useState({ turns: 0, totalCostUsd: 0, totalLatencyMs: 0, turnLog: [] });
  // Per-tab search state. `searched` is what lets a zero-result search still
  // surface its chip; `lowRelevance` mirrors the notice the explicit answer shows.
  const [resultsMeta, setResultsMeta] = useState({ bugs: {}, cases: {} });
  const streamingMsgIdRef = useRef(null);
  const activeSourceTurnRef = useRef(null);
  const sourceReplacementMsgIdRef = useRef(null);
  const sourceReplacementSnapshotRef = useRef(null);
  // What onSourceReplacementStarted removed besides the answer itself — the
  // followups row, trailing status rows — and the card that was open. A failed
  // or stopped regeneration puts all of it back, not just the answer bubble.
  const sourceReplacementTailRef = useRef(null);
  // The card this tab just put back from that snapshot. The server re-emits the
  // same card (source_replacement_restore) for tabs that have no snapshot; a tab
  // that does must not paint the menu twice.
  const restoredQuestionRef = useRef(null);
  // Everything the model has emitted for the streaming message, before the
  // APP_INTENT:CLOSE_SESSION checkpoint is cut out of it. The rendered text is
  // derived from this rather than appended to, because the marker arrives a few
  // characters at a time and cannot be recognised from one delta.
  const streamRawRef = useRef("");
  // Set when this turn's answer carried the checkpoint; consumed at tse:done,
  // where the wrap-up is complete and the close can be fired once.
  const closeIntentRef = useRef(null);
  // The socket emitter, reachable from callbacks defined above the hook call.
  const closeSessionRef = useRef(null);
  const closeAfterTeachRef = useRef(false);
  // Set by onJoined when the server reports the session it just rejoined is
  // CLOSED. Read by onReplay rather than acted on directly: replay does
  // setMessages(replayed) — a REPLACE — so a banner appended from the join
  // handler is wiped a few milliseconds later by the transcript it belongs to.
  const resumedClosedRef = useRef(false);
  const turnStartRef = useRef(0);
  // Set fresh on every handleSend; consumed by the next genuine turn-end
  // onIdle (see the isFiniteCost gate there — tse:idle also fires on session
  // (re)join, unrelated to any turn). fast_answer can treat a typed message
  // as an unrelated aside rather than an answer, leaving the diagnosis exactly
  // where it was — but handleSend clears currentQuestion optimistically for
  // EVERY send (it can't know in advance which case this is), so without this
  // the still-pending question would vanish from screen even though the
  // backend never advanced past it. Explicitly cleared wherever currentQuestion
  // is genuinely resolved (onQuestion, handleAnswer, handleAction, handleClear,
  // onSessionExpired, onMode's fast-mode branch, handleExitGuided) so a stale
  // snapshot from an earlier, already-resolved turn can never survive to be
  // wrongly restored on top of unrelated later state.
  const pendingQuestionSnapshotRef = useRef(null);
  // Implicit sidebar search: cases on turns 3,6,9…; bugs on turns 4,7,10…
  const copilotTurnRef = useRef(0);
  const implicitSearchRef = useRef(null);
  // One catch-up implicit search per mount (see onJoined).
  const implicitCatchupFiredRef = useRef(false);
  // A turn spans several emits (inform_user, then ask_user_input). Cost is
  // accumulated across them and banked once, when control returns to the user.
  const turnPendingRef = useRef(false);

  const isTransientCopilotRow = (m) => {
    if (m.type === "status" || m.isStreaming) return true;
    const t = String(m.text || "");
    if (/^Searching search_/i.test(t)) return true;
    if (/\bSearching\b/i.test(t) && /\bsearch_(bugs|cases)\b/i.test(t)) return true;
    return false;
  };

  const purgeTransientMessages = useCallback(() => {
    setMessages((prev) => prev.filter((m) => !isTransientCopilotRow(m)));
  }, []);

  const clearCopilotTurnState = useCallback(() => {
    setIsStreaming(false);
    turnPendingRef.current = false;
    streamingMsgIdRef.current = null;
    setCurrentQuestion(null);
    purgeTransientMessages();
  }, [purgeTransientMessages]);

  useEffect(() => {
    if (escalationActive && variant === "enterprise") {
      purgeTransientMessages();
    }
  }, [escalationActive, variant, purgeTransientMessages]);

  // Cost is reported as a session-cumulative figure; a turn's cost is the delta
  // between the latest total and the total at the previous turn boundary.
  // Plus the itemised cost lines the server pairs with that total (utils/turnCost.js): a banked turn's rows are the
  // lines in its window, so they add up to its cost; the unitemised rest is an explicit "other" row.
  const costLedgerRef = useRef(createCostLedger());
  // Step timings from the last tse:done, waiting to be banked with their turn.
  const latestBreakdownRef = useRef(null);
  // First agent message of the session — feedback metadata, and the flag that
  // says a session actually exists to give feedback on.
  const firstPromptRef = useRef(null);
  // The product name, remembered the moment it is known. The source-map handoff
  // needs it for its fallback fetch, and scanning `messages` for the "product"
  // block is not enough: a resumed session can carry payload.product while its
  // replayed history has no product block to find.
  const productRef = useRef("");
  // Everything the agent has been told about the call, newest last. One entry
  // per "send to Copilot" click; the agent sees them joined. Kept as a ref
  // beside the message list rather than derived from it because the message
  // list is append-only UI state and this is the wire payload. Declared here,
  // with firstPromptRef, because handleAction reads it as the other half of
  // the "is this session worth searching" test.
  const callContextBlocksRef = useRef([]);
  // Same list as state, because GlobalContextPanel renders it and a ref does
  // not re-render. The ref stays the read path for handlers (it is current
  // within the same click, which the state is not); writes go through
  // setCallContextBlocks below so the two can never disagree.
  const [callContextBlocks, setCallContextBlocksState] = useState([]);
  const [contextPanelOpen, setContextPanelOpen] = useState(false);
  // What the server actually holds (session.call_context is this join), so the
  // header button can show the real size rather than a block count alone.
  const callContextChars = useMemo(
    () => callContextBlocks.map((b) => b.text).filter(Boolean).join("\n\n").length,
    [callContextBlocks],
  );
  const setCallContextBlocks = useCallback((next) => {
    const list = typeof next === "function" ? next(callContextBlocksRef.current) : next;
    callContextBlocksRef.current = list;
    setCallContextBlocksState(list);
  }, []);
  // Last freeform (unmatched-option) message the engineer sent. When the very
  // next question carries context_changed, this is the message that caused
  // it — captured into contextSwitchQueryRef so the post-close banner can
  // offer to restart a session seeded with it.
  const lastFreeTextRef = useRef("");
  const contextSwitchQueryRef = useRef(null);
  // Fix 3: query deferred until tse:cleared confirms the new session id.
  const deferredSendRef = useRef(null);
  // ── Close-and-continue: a clear is in flight ──────────────────────────
  // Set by handleClear BEFORE it re-joins (that re-join is what provokes the
  // old session's tse:joined + tse:replay), cleared by onCleared once
  // sessionIdRef has been swapped to the successor and the successor's own
  // join has gone out. While it is set, no join ack and no replay may seed
  // this tab's state.
  //
  // Why an id comparison was not enough. onReplay/onJoined already drop a
  // payload whose session_id is not sessionIdRef.current — but during a clear
  // that ref is ITSELF mid-flight, so which of the two messages wins the race
  // decides whether the guard works:
  //   • tse:cleared first (fast loopback, local dev): sessionIdRef is already
  //     the successor, the parent's late replay mismatches, guard drops it.
  //     This is why the bug never reproduced on a dev machine.
  //   • tse:replay first (real latency, EC2): sessionIdRef is STILL the parent,
  //     so the ids MATCH, the guard passes, and the closed parent's entire
  //     transcript — wrap-up, closed banner, feedback row — is repainted over
  //     the just-cleared thread; onCleared then swaps in the child id and
  //     navigates. Observed in production: child URL, parent conversation,
  //     parent turn count in the header, correct again after a refresh
  //     (storage was always right).
  // A flag that spans the whole exchange is ordering-independent: during a
  // clear there is no replay worth painting at all. The parent's is being
  // discarded by definition, and the successor's arrives from the join issued
  // in onCleared, after this flag is down. Do not "simplify" it back to an id
  // comparison.
  const clearInFlightRef = useRef(false);
  const clearInFlightTimerRef = useRef(null);
  const endClearInFlight = useCallback(() => {
    clearInFlightRef.current = false;
    if (clearInFlightTimerRef.current) {
      clearTimeout(clearInFlightTimerRef.current);
      clearInFlightTimerRef.current = null;
    }
  }, []);
  const beginClearInFlight = useCallback(() => {
    clearInFlightRef.current = true;
    // Re-armed, not stacked: a second clear before the first was acknowledged
    // restarts the window rather than inheriting the older deadline.
    if (clearInFlightTimerRef.current) clearTimeout(clearInFlightTimerRef.current);
    clearInFlightTimerRef.current = setTimeout(() => {
      // tse:cleared never came (server error, dropped socket). Release anyway —
      // a stuck flag would silently swallow every later replay on this mount,
      // so the next refresh or reconnect would show an empty thread.
      clearInFlightRef.current = false;
      clearInFlightTimerRef.current = null;
    }, CLEAR_INFLIGHT_GRACE_MS);
  }, []);
  // Stable ref to the socket instance for use in callbacks defined before
  // useTseSocket — sendMessage closes over a stale sessionId, so onCleared
  // emits directly with the just-updated sessionIdRef.
  const socketInstanceRef = useRef(null);
  // Set once the hook exists (it is created below onSessionCreated, which needs
  // it), so the session-created handler can bind the id without a render.
  const adoptSessionRef = useRef(null);
  const joinRef = useRef(null);
  const sendGuard = useTurnSendGuard(isStreaming);
  const inFlightRejoin = useInFlightRejoin(() => joinRef.current?.());
  const commandBusy = useCommandBusy({ isStreamingRef, setIsStreaming });
  // Set once useTseSocket exists (it is created below onAccessDenied, which needs
  // it), so the denial handler can force a re-authenticated reconnect.
  const reauthenticateRef = useRef(null);
  // Attempts spent on the current burst of retryable denials, with the stamp the
  // budget is measured from. A ref: the retries happen inside socket callbacks,
  // between renders.
  const authRetryRef = useRef({ count: 0, startedAt: 0 });
  // Whatever the engineer did that required a session, held until there is one.
  // A list, not a slot: two actions can land inside one mint round trip (send a
  // message, then hit Find Cases), and a slot would silently drop the first.
  const pendingIntentsRef = useRef([]);
  // A tse:start is out. Stops a fast second action asking for a second session.
  const startPendingRef = useRef(false);
  const startTimeoutRef = useRef(null);
  // The first message, kept aside so the optimistic Recents row can be drawn
  // once the id it belongs to arrives.
  const pendingTitleRef = useRef(null);
  // pushCallContext is defined far below; the created-handler needs to call it.
  const pushCallContextRef = useRef(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackNotice, setFeedbackNotice] = useState("");
  const feedbackNoticeTimerRef = useRef(null);
  // { action, message } | null — ActionBar renders this right below the
  // specific button it's about, unlike feedbackNotice above which sits next
  // to the (differently positioned) header Feedback button.
  const [actionNotice, setActionNotice] = useState(null);
  const actionNoticeTimerRef = useRef(null);
  // Zoom call binding — "paste the meeting link back" modal. See
  // hooks/useZoomBridgeSocket.js and sockets_zoom.py's zoom:bind.
  const [showZoomModal, setShowZoomModal] = useState(false);
  // Root Cause Analysis mode (ActionBar toggle). Off by default: attaching
  // context is how an engineer keeps the Copilot current mid-call, and most of
  // those moments are not a request for an answer. Turning it on says "every
  // time I hand you the call, analyse it".
  const [autoRca, setAutoRca] = useState(false);

  // The copilot's per-message actions target the CUSTOMER panel on the left,
  // matching Dashboard2: "Send to customer" posts the answer to the customer,
  // "Edit in customer chat" loads it into that panel's composer first.
  const customerPanelRef = useRef(null);
  // The reverse direction: "Start Diagnosis from Call" (left panel) drops its
  // condensed result into THIS panel's composer — see onTranscriptCondensed.
  const copilotPanelRef = useRef(null);

  const sidebar = useSidebar();
  // V5 diagnosis popover (W7b): latest tse:diag_state per session, (diagnosis_id, seq) ordered.
  const brain = useBrainState(sessionId);
  const { applyDiagState, applyCommandResult, reset: resetBrain } = brain;
  const sessionLearning = useSessionLearning({
    sessionId,
    diagState: brain.diagState,
    markPending: brain.markCommandPending,
    clearPending: brain.clearCommandPending,
  });
  // Cases/Bugs overlay state reported by CopilotPanel: the popover collapses to its bubble while it is open.
  const [resultsOverlayOpen, setResultsOverlayOpen] = useState(false);
  // "See what was learned" / "Teach the cause": open the popover on a tab ({tab, section, nonce}).
  const [brainOpenRequest, setBrainOpenRequest] = useState(null);

  const appendMessage = useCallback((msg) => {
    if (escalationActiveRef.current && variant === "enterprise" && isTransientCopilotRow(msg)) {
      return;
    }
    // turnNo is stamped HERE or it is never stamped at all: every correction
    // ever reported carried turn_no = null (15/15 rows), because the flag
    // handlers read `msg?.turnNo` and nothing ever set it. copilotTurnRef is
    // incremented by bankTurn AFTER a turn commits, so the turn currently in
    // flight is current + 1 — which lines up with the server's own numbering
    // (a trail's guided-entry steps carry turn 2, the first user turn is 1).
    const turnNo = msg?.turnNo ?? copilotTurnRef.current + 1;
    setMessages((prev) => [...prev, { id: msg.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`, timestamp: Date.now(), ...msg, turnNo }]);
  }, [variant]);

  const patchMessage = useCallback((id, patch) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...(typeof patch === "function" ? patch(m) : patch) } : m)));
  }, []);

  // V5 DA narration (tse:da_narration_*): the DA turn's text as a copilot bubble keyed by message_id,
  // source cards above it, after the card. Enterprise never receives it (server skips V5 there).
  const hasMessage = useCallback((id) => messagesRef.current.some((m) => m.id === id), []);
  const narration = useDaNarration({
    appendMessage, patchMessage, setMessages, hasMessage, enabled: variant !== "enterprise",
  });
  const {
    onDaNarrationPending, onDaNarrationStart, onDaNarrationDelta, onDaNarrationEnd, onDaNarrationAbort,
    finalizeNarration, resetNarration,
  } = narration;

  const enableLatestSourceReplacement = useCallback(() => {
    const turnId = activeSourceTurnRef.current;
    if (!turnId) return;
    setMessages((prev) => prev.map((message) => ({
      ...message,
      canReplaceSource: (
        message.sourceTurnId === turnId
        && Array.isArray(message.sources)
        && message.sources.length > 0
      ),
    })));
  }, []);

  const lockSourceReplacement = useCallback(() => {
    activeSourceTurnRef.current = null;
    setMessages((prev) => prev.map((message) => (
      message.canReplaceSource
        ? { ...message, canReplaceSource: false, replacingSourceNumber: null, sourceActionKind: null }
        : message
    )));
  }, []);

  const onChunk = useCallback((payload) => {
    if (escalationActiveRef.current && variant === "enterprise") return;
    // Tokens are arriving, so whatever the join was waiting for is moot.
    endHydrate();
    const delta = payload?.delta ?? payload?.text ?? "";
    if (!delta) return;
    // A regeneration's tokens belong to the answer being rewritten, never to a
    // new bubble. Stop clears streamingMsgIdRef at once, but tokens already on
    // the wire still arrive before the server's cancel lands — and with no id
    // they opened a second bubble holding the tail of an answer that is about
    // to be thrown away. With no regeneration bubble on this tab, drop them.
    if (payload?.source_replacement) {
      const target = sourceReplacementMsgIdRef.current;
      if (!target) return;
      streamingMsgIdRef.current = target;
    }
    setIsStreaming(true);
    if (!streamingMsgIdRef.current) {
      // New streaming message = new turn. Clear both, so nothing a previous
      // turn recognised can leak into this one.
      streamRawRef.current = "";
      closeIntentRef.current = null;
    }
    streamRawRef.current += delta;
    // A hybrid answer has no tools, so "the engineer is done" arrives as text:
    // APP_INTENT:CLOSE_SESSION <wrap-up>. Strip the marker before it is painted
    // and remember the intent for tse:done, which is where the wrap-up is
    // complete and the close is fired.
    const { text, close, summary } = splitCloseIntent(streamRawRef.current);
    if (close) closeIntentRef.current = summary;
    if (!streamingMsgIdRef.current) {
      const id = `bot-${Date.now()}`;
      streamingMsgIdRef.current = id;
      appendMessage({ id, sender: "copilot", text, isStreaming: true, isDone: false });
    } else {
      // A close reply cites nothing — it is a wrap-up written from the
      // conversation, not an answer off the pool. The cards landed ~1.5s before
      // the first token, so by the time the checkpoint is recognised they are
      // already on screen; drop them here and refuse the later ones below.
      patchMessage(streamingMsgIdRef.current, () => (
        close
          ? { text, isStreaming: true, isDone: false, sources: [], sourcesPreview: false }
          : { text, isStreaming: true, isDone: false }
      ));
    }
  }, [appendMessage, patchMessage, endHydrate, variant]);

  // Decorates the streaming bubble, and opens one when the cards arrive first.
  //
  // The original rule here was "never create a bubble" — an empty one would sit
  // there with a blinking cursor until the answer arrived. That still holds, and
  // is why the bubble below starts isStreaming:false: the cursor is CSS on
  // isStreaming && !isDone, so nothing blinks until onChunk flips it. What the
  // bubble is NOT is empty — it carries the retrieved source cards, which land
  // ~1.5s ahead of the first token and are the whole point of the fast stage.
  const onSourcesPreview = useCallback((payload) => {
    if (escalationActiveRef.current && variant === "enterprise") return;
    const sources = payload?.sources || [];
    if (!sources.length) return;
    // The SERVER says which previews belong to a replaceable turn. Falling back
    // to the active token tagged guided-tool previews too, and offered a Cite
    // the server always refuses.
    const sourceTurnId = payload?.turn_id || null;
    const sourceAlternatives = Array.isArray(payload?.alternatives)
      ? payload.alternatives
      : [];
    if (sourceTurnId) activeSourceTurnRef.current = sourceTurnId;
    // This turn is ending the session, not answering from documents.
    if (closeIntentRef.current !== null) return;
    // Same rule as onChunk: a regeneration's cards go on the bubble being
    // rewritten or nowhere.
    if (payload?.source_replacement) {
      if (!sourceReplacementMsgIdRef.current) return;
      streamingMsgIdRef.current = sourceReplacementMsgIdRef.current;
    }
    const id = streamingMsgIdRef.current;
    if (id) {
      patchMessage(id, {
        sources,
        sourcesPreview: true,
        sourceTurnId,
        sourceAlternatives,
        canReplaceSource: false,
      });
      return;
    }
    const newId = `bot-${Date.now()}`;
    streamingMsgIdRef.current = newId;
    appendMessage({
      id: newId, sender: "copilot", text: "",
      isStreaming: false, isDone: false,
      sources, sourcesPreview: true, sourceTurnId, sourceAlternatives,
      canReplaceSource: false,
    });
  }, [appendMessage, patchMessage, variant]);

  const onSourcesEnriched = useCallback((payload) => {
    if (escalationActiveRef.current && variant === "enterprise") return;
    const sources = payload?.sources || [];
    if (closeIntentRef.current !== null) return;
    const id = payload?.source_replacement
      ? sourceReplacementMsgIdRef.current
      : streamingMsgIdRef.current;
    if (id) patchMessage(id, (message) => ({
      sources,
      sourcesPreview: false,
      // From the payload only (see onSourcesPreview). The guided manifest
      // enrichments carry none and keep whatever the bubble already had.
      sourceTurnId: payload?.turn_id || message.sourceTurnId || null,
      sourceAlternatives: Array.isArray(payload?.alternatives)
        ? payload.alternatives
        : message.sourceAlternatives,
    }));
  }, [patchMessage, variant]);

  const onSourceReplacementStarted = useCallback((payload) => {
    const turnId = payload?.turn_id;
    if (!turnId || turnId !== activeSourceTurnRef.current) return;
    const currentMessages = messagesRef.current;
    const index = currentMessages.findIndex(
      (message) => message.sourceTurnId === turnId
    );
    if (index < 0) return;
    const current = currentMessages[index];
    sourceReplacementSnapshotRef.current = current;
    // Everything removed below besides the answer, and the card being cleared:
    // the followups row, trailing status rows, the open question. Only a
    // successful rewrite replaces them (the server sends the new menu).
    sourceReplacementTailRef.current = {
      messages: currentMessages.slice(index + 1),
      question: currentQuestionRef.current,
    };
    restoredQuestionRef.current = null;
    sourceReplacementMsgIdRef.current = current.id;
    streamingMsgIdRef.current = current.id;
    streamRawRef.current = "";
    closeIntentRef.current = null;
    setCurrentQuestion(null);
    setMessages((prev) => {
      const liveIndex = prev.findIndex((message) => message.id === current.id);
      if (liveIndex < 0) return prev;
      const sourceNumber = Number(String(payload?.source_id || "").replace(/^S/i, ""));
      return [
        ...prev.slice(0, liveIndex),
        {
          ...prev[liveIndex],
          text: "",
          isStreaming: true,
          isDone: false,
          sourcesPreview: true,
          canReplaceSource: false,
          replacingSourceNumber: Number.isFinite(sourceNumber) ? sourceNumber : null,
          sourceActionKind: payload?.action === "cite" ? "cite" : "replace",
        },
      ];
    });
    setIsStreaming(true);
  }, []);

  const onSourceReplacementComplete = useCallback((payload) => {
    const turnId = payload?.turn_id;
    const id = sourceReplacementMsgIdRef.current;
    if (!id || turnId !== activeSourceTurnRef.current) return;
    patchMessage(id, (message) => ({
      replacingSourceNumber: null,
      sourceActionKind: null,
      sourceAlternatives: Array.isArray(payload?.alternatives)
        ? payload.alternatives
        : message.sourceAlternatives,
      canReplaceSource: true,
      isStreaming: false,
      isDone: true,
      sourcesPreview: false,
    }));
    sourceReplacementMsgIdRef.current = null;
    sourceReplacementSnapshotRef.current = null;
    sourceReplacementTailRef.current = null;
    streamingMsgIdRef.current = null;
    setIsStreaming(false);
    if (payload?.cite_missed) {
      const sourceNumber = String(payload?.source_id || "").replace(/^S/i, "");
      appendMessage({
        type: "status",
        text: `The regenerated answer still did not use source ${sourceNumber}. It may not address this question.`,
      });
    }
  }, [appendMessage, patchMessage]);

  const onSourceReplacementError = useCallback((payload) => {
    const id = sourceReplacementMsgIdRef.current;
    const snapshot = sourceReplacementSnapshotRef.current;
    if (payload?.rejected && id) {
      // Refused before it started (e.g. a second click), while THIS tab is
      // showing a regeneration that is still running: leave that one alone.
      appendMessage({ type: "error", text: payload?.message || "That source could not be changed." });
      return;
    }
    if (id && snapshot) {
      const tail = sourceReplacementTailRef.current || { messages: [], question: null };
      // The old answer AND what followed it. Restoring only the bubble left the
      // old answer with no followups menu, no trailing rows and no open card —
      // and the server had already retired the waiter behind that card.
      setMessages((prev) => {
        const at = prev.findIndex((message) => message.id === id);
        if (at < 0) return prev;
        return [
          ...prev.slice(0, at),
          {
            ...snapshot,
            replacingSourceNumber: null,
            sourceActionKind: null,
            canReplaceSource: Array.isArray(snapshot.sources) && snapshot.sources.length > 0,
            isStreaming: false,
            isDone: true,
            sourcesPreview: false,
          },
          ...(tail.messages || []),
          // Anything appended since (a straggler row) stays, after the tail.
          ...prev.slice(at + 1),
        ];
      });
      if (tail.question) {
        setCurrentQuestion(tail.question);
        restoredQuestionRef.current = tail.question;
      }
      sourceReplacementMsgIdRef.current = null;
      sourceReplacementSnapshotRef.current = null;
      sourceReplacementTailRef.current = null;
      streamingMsgIdRef.current = null;
      setIsStreaming(false);
    } else {
      // Rejected before it started: only the clicked card was marked busy, and
      // whatever else is streaming right now belongs to someone else.
      const turnId = activeSourceTurnRef.current;
      setMessages((prev) => prev.map((message) => (
        message.replacingSourceNumber != null
          ? {
              ...message,
              replacingSourceNumber: null,
              sourceActionKind: null,
              canReplaceSource: !!turnId && message.sourceTurnId === turnId,
            }
          : message
      )));
    }
    // A Stop is the engineer's own action, not a failure: said as a status
    // row ("the original answer is kept"), not painted as an error.
    appendMessage({
      type: payload?.stopped ? "status" : "error",
      text: payload?.message || (payload?.stopped
        ? "Stopped. The original answer is kept."
        : "The source could not be replaced. Please try again."),
    });
  }, [appendMessage]);

  /**
   * Record one completed turn. Called when control returns to the user —
   * either the agent finished (tse:done with turn_complete) or it asked a
   * question. Guarded so a turn is banked exactly once.
   */
  const bankTurn = useCallback((cacheHit = false) => {
    if (!turnPendingRef.current) return;
    turnPendingRef.current = false;

    const latencyMs = turnStartRef.current ? Date.now() - turnStartRef.current : 0;
    turnStartRef.current = 0;
    // Captured before clear: setSessionStats updater runs async, so reading the
    // ref inside the updater would always see null (already cleared by line below).
    const { costUsd, steps } = bankTurnCost(costLedgerRef.current, latestBreakdownRef.current || []);
    latestBreakdownRef.current = null;

    setSessionStats((prev) => ({
      turns: prev.turns + 1,
      totalCostUsd: prev.totalCostUsd + costUsd,
      totalLatencyMs: prev.totalLatencyMs + latencyMs,
      turnLog: [
        ...prev.turnLog,
        { n: prev.turns + 1, latencyMs, costUsd, cacheHit, steps },
      ],
    }));

    // Control is back with the user, so the turn is committed server-side and
    // Recents can be asked for the real row (title, timestamp) — which replaces
    // the optimistic one handleSend inserted. Forced past the cooldown by the
    // listener in SideBar; nothing else in the app dispatches this.
    window.dispatchEvent(new Event(RECENTS_REFRESH_EVENT));

    copilotTurnRef.current += 1;
    const turn = copilotTurnRef.current;
    const fireImplicit = implicitSearchRef.current;
    if (fireImplicit && !(escalationActiveRef.current && variant === "enterprise")) {
      if (turn >= 3 && turn % 3 === 0) fireImplicit("cases", 5);
      if (turn >= 4 && (turn - 1) % 3 === 0) fireImplicit("bugs", 5);
    }
  }, [variant]);

  const onQuestion = useCallback((rawPayload) => {
    if (escalationActiveRef.current && variant === "enterprise") return;
    // Diagnosis cards belong to the engineer view only.
    if (variant === "enterprise" && rawPayload?.diagnosis_id) return;
    if (inFlightRejoin.consumeJoinQuestion()) return;
    // W2 option objects {option_id, label} -> labels for the card, ids kept in
    // optionObjects for the kind:"answer" reply (questionAnswer.js). Legacy
    // bare-string payloads pass through unchanged.
    const payload = normalizeQuestion(rawPayload);
    // A resumed session with a still-pending question gets it re-emitted by
    // _resume_client — so this is one of the ways the join comes back.
    endHydrate();
    // The card a failed / stopped source regeneration had cleared, re-emitted by
    // the server for tabs that hold no snapshot of it. This tab already put it
    // back from its own (onSourceReplacementError) — painting it again would
    // show the followups menu twice. Anything else is processed normally.
    if (payload?.source_replacement_restore) {
      const restored = restoredQuestionRef.current;
      restoredQuestionRef.current = null;
      const same = restored
        && String(restored.question || "") === String(payload?.question || "")
        && JSON.stringify(restored.options || []) === JSON.stringify(payload?.options || []);
      if (same) {
        setIsStreaming(false);
        enableLatestSourceReplacement();
        return;
      }
    }
    // A genuinely new question supersedes anything a prior turn's handleSend
    // left pending for onIdle to maybe restore — without this, answering that
    // NEW question (which never touches this ref) leaves the old snapshot
    // armed for a LATER, unrelated onIdle to wrongly restore.
    pendingQuestionSnapshotRef.current = null;
    // The out-of-context gate marks the question that introduces it with
    // context_changed — the freeform message that triggered it is whatever
    // was sent right before, still sitting in lastFreeTextRef. Cleared (not
    // just left unset) on any question without the flag — same "new question
    // supersedes a stale ref" reasoning as pendingQuestionSnapshotRef above:
    // without this, declining the gate ("No — stay") would leave the deferred
    // query armed for a LATER, unrelated close to wrongly offer it.
    contextSwitchQueryRef.current = payload?.context_changed ? (lastFreeTextRef.current || null) : null;
    // Stamped on arrival: the frozen card in the transcript should say when the
    // question was PUT to you, not when you got round to answering it.
    const askedAt = Date.now();
    setCurrentQuestion({ ...payload, askedAt });
    setIsStreaming(false);
    sendGuard.release();
    // A DA card is not its turn's end: the narration follows it, and the V5 tse:idle closes the turn.
    if (!payload?.diagnosis_id) inFlightRejoin.noteTurnEnd();
    // Followup suggestions read as chat, not as a question to answer, so they
    // go into the transcript as bubbles and CopilotPanel suppresses the
    // composer card for them. currentQuestion is still set above ON PURPOSE:
    // the pending-answer protocol (handleAnswer / handleSend routing, the
    // composer placeholder) all key off it, so clearing it here would leave
    // the reply unrouted and hang the turn.
    if (String(payload?.question || "") === FOLLOWUPS_MARKER) {
      appendMessage({
        type: "followups",
        options: Array.isArray(payload?.options) ? payload.options : [],
        askedAt,
      });
    }
    // tse:question now carries breakdown so short inform_user messages (<80 chars
    // going through tse:info with no tse:done) still land their steps in the turn
    // panel. Also covers any race where tse:question beats tse:done.
    if (payload?.breakdown?.length) {
      latestBreakdownRef.current = payload.breakdown;
    }
    // ask_user_input hands control back — the turn is over even though the
    // agent emitted no terminal "assistant" message. A cache-served turn
    // reports turn_complete:false on tse:done (live semantics, a card is still
    // to be painted), so it is banked HERE rather than by onDone — carry the
    // hit flag through or the turn-log row loses its ⚡.
    // Session-cumulative cost, read BEFORE banking for the same reason: this
    // event banks the turn, so the total must be current or the turn's cost
    // lands on the next turn.
    absorbCostPayload(costLedgerRef.current, payload);
    enableLatestSourceReplacement();
    // A Diagnostic Agent card is NOT the end of its turn: the DA narration (and its model cost) follows the card, and the
    // turn ends with the V5 tse:idle that carries session_cost_usd + breakdown (sockets_tse._finish_turn_locally).
    if (!payload?.diagnosis_id) bankTurn(!!payload?.cache_hit);
    // Deliberately does NOT clear streamingMsgIdRef: agent tools run in
    // parallel, so tse:question can land while chunks are still draining.
    // Clearing here would send the remaining chunks into a second bubble and
    // let the trailing tse:done patch the wrong message. tse:done owns teardown.
  }, [bankTurn, appendMessage, endHydrate, variant, enableLatestSourceReplacement, sendGuard, inFlightRejoin]);

  const onInfo = useCallback((payload) => {
    if (escalationActiveRef.current && variant === "enterprise") return;
    // The session's learning switch changed (server note, also stored for replay and export): a kept chat line, not a
    // progress row.
    if (payload?.kind === "learning_note") {
      sessionLearning.noteServerValue(payload.session_id, payload.enabled !== false);
      if (variant !== "enterprise" && payload.text) {
        appendMessage({ type: "learning_note", text: payload.text, enabled: payload.enabled !== false });
      }
      return;
    }
    const product = payload?.product;
    if (product) {
      appendMessage({
        type: "product",
        text: `Product identified: ${product}`,
        product,
        products: Array.isArray(payload?.products) ? payload.products : [],
        canChange: !!payload?.can_change,
      });
      setIsStreaming(true);
      productRef.current = product;
    } else {
      // `query` is carried through so the status row can show WHAT was searched
      // for. For cases and bugs that text is not the engineer's words -- it is a
      // factual summary of the conversation built server-side -- so it is the
      // one input that explains the records that come back.
      appendMessage({
        type: "status",
        text: payload?.text || payload?.message || "Working…",
        query: payload?.query || "",
        tool: payload?.tool || "",
      });
    }
  }, [appendMessage, variant, sessionLearning]);

  const onProductLocked = useCallback(() => {
    setProductLocked(true);
    setChangePending(null);
  }, [setChangePending]);

  // Progress line while the agent is still working. Same shape onInfo already uses
  // for short text, so CopilotPanel renders it with the existing status row.
  const onStatus = useCallback((payload) => {
    if (escalationActiveRef.current && variant === "enterprise") return;
    const text = payload?.text || payload?.message || "";
    if (text) appendMessage({ type: "status", text });
  }, [appendMessage, variant]);

  const onBugs = useCallback((payload) => {
    sidebar.setBugs(payload?.bugs || payload || []);
    setResultsMeta((prev) => ({
      ...prev,
      bugs: {
        searched: payload?.searched !== false,
        lowRelevance: !!payload?.low_relevance,
        sectionText: payload?.section_text || "",
        query: payload?.query || "",
      },
    }));
  }, [sidebar]);

  const onCases = useCallback((payload) => {
    sidebar.setCases(payload?.cases || payload || []);
    setResultsMeta((prev) => ({
      ...prev,
      cases: {
        searched: payload?.searched !== false,
        lowRelevance: !!payload?.low_relevance,
        sectionText: payload?.section_text || "",
        query: payload?.query || "",
      },
    }));
  }, [sidebar]);

  /** Same Case Analysis panels as /case-analysis/:id, rendered as a chat bubble. */
  const onCaseAnalysis = useCallback((payload) => {
    const analysis = payload?.analysis;
    if (!analysis) return;
    streamingMsgIdRef.current = null;
    setIsStreaming(false);
    appendMessage({
      type: "case_analysis",
      sender: "copilot",
      analysis,
      isDone: true,
    });
  }, [appendMessage]);

  const onDone = useCallback((payload) => {
    // Traversal rides along with tse:done — Socket.IO has no REST body to carry it.
    // THE MAP IS A GUIDED-WALKTHROUGH-ONLY ARTIFACT (D2). The map advances and
    // the step-map button appears ONLY for guided turns. Non-guided turns must
    // not call applyTurn (it would advance the map on a turn that never walked
    // anything) and must not show step/diag map buttons.
    // `payload.guided` is the server's flag on this turn, with a fallback to
    // copilotModeRef for servers that predate the field.

    // Step timings are NOT attached to the message — they belong to the turn, and
    // the header's Turn breakdown panel is where turn-level numbers already live.
    // Held in a ref until bankTurn folds them into that turn's log entry.
    //
    // Read SYNCHRONOUSLY, here at the top. This used to live inside the
    // patchMessage updater below, whose callback React defers to render time —
    // so bankTurn (called at the end of this handler) always read a null ref and
    // banked steps: [], leaving every row unexpandable. It also meant a turn with
    // no streaming message id (id === null) dropped its breakdown entirely.
    if (payload?.breakdown?.length) {
      latestBreakdownRef.current = payload.breakdown;
      // tse:question can land before tse:done (see onQuestion), which banks the
      // turn while this ref is still empty and leaves the row unexpandable. If
      // the turn is already banked, backfill it instead of waiting for a bank
      // that has been and gone. Guarded on turnPendingRef so a turn still in
      // flight keeps the normal path and cannot steal the previous turn's row.
      if (!turnPendingRef.current) {
        const steps = payload.breakdown;
        setSessionStats((prev) => {
          const last = prev.turnLog[prev.turnLog.length - 1];
          if (!last || last.steps?.length) return prev;
          const turnLog = [...prev.turnLog];
          turnLog[turnLog.length - 1] = { ...last, steps };
          return { ...prev, turnLog };
        });
        latestBreakdownRef.current = null;
      }
    }

    // Read SYNCHRONOUSLY, like the breakdown above and for the same reason: the
    // patchMessage updater below is deferred to render time, which is AFTER the
    // close fires further down. Setting closeIntentRef in there left it dirty
    // once the fire had already cleared it — so the NEXT turn opened with a
    // stale summary and closed a live session with the previous one's wrap-up
    // (observed 2026-08-21: a ZPA question closed with a ZCC/ISP wrap-up).
    const final = payload?.final_text ? splitCloseIntent(payload.final_text) : null;
    if (final?.close) closeIntentRef.current = final.summary;

    // A regeneration finalises the bubble it is rewriting, even after Stop
    // cleared streamingMsgIdRef (its tse:done can still be on the wire).
    const id = payload?.source_replacement
      ? sourceReplacementMsgIdRef.current
      : streamingMsgIdRef.current;
    if (id) {
      patchMessage(id, () => {
        const patch = { isStreaming: false, isDone: true, sourcesPreview: false };
        // Cache-hit flag from the server — only present on turns served from
        // the semantic cache. Absent (not false) on live turns, so the badge
        // only renders when the server explicitly says so.
        if (payload?.cache_hit) patch.cacheHit = true;
        if (final) {
          // final_text replaces what was streamed, marker and all — strip it
          // here too or the checkpoint reappears at the end of the turn.
          patch.text = final.text;
          if (final.close) patch.sources = [];
        }
        // Enrichment never arrived — the answer cited nothing this turn. The cards
        // STAY: these are the documents retrieval actually returned, and their real
        // match % is the evidence that the pool was weak. Dropping them left an empty
        // row, which reads as "nothing was searched" rather than "nothing scored well
        // enough to cite" — two very different things when debugging retrieval.
        //
        // Nothing shimmers: `sourcesPreview: false` above clears isPreview, and
        // SourceCard only shimmers while isPreview is set, so an uncited card renders
        // as title + score with no "Why selected" — which is exactly what it is.
        return patch;
      });
    }
    // inform_user finalises a message but not the turn — the agent still has a
    // model round-trip to make before the question arrives. Keep the thinking
    // indicator up and don't bank a turn until the turn actually completes.
    const turnComplete = payload?.turn_complete !== false;
    // The answer was the APP_INTENT:CLOSE_SESSION checkpoint. Fire the close
    // here rather than the moment the marker was recognised mid-stream: the
    // wrap-up after it is still arriving then, and it is what the close shows.
    //
    // Deliberately NOT gated on turnComplete — a hybrid answer always reports
    // turn_complete:false (the guided-session offer normally follows it), so
    // gating would mean this never fires on the one path it exists for.
    if (closeIntentRef.current !== null) {
      const summary = closeIntentRef.current;
      closeIntentRef.current = null;
      closeSessionRef.current?.(summary);
    }
    streamRawRef.current = "";
    streamingMsgIdRef.current = null;
    // Only ever clears it. setIsStreaming(!turnComplete) also *raised* the flag
    // on every mid-turn inform_user, and nothing downstream was obliged to
    // lower it again — tse:idle is what ends the turn now.
    if (turnComplete) {
      setIsStreaming(false);
      sendGuard.release();
    }
    // Session-cumulative cost; the per-turn figure is derived in bankTurn.
    absorbCostPayload(costLedgerRef.current, payload);
    if (turnComplete) bankTurn(!!payload?.cache_hit);
  }, [patchMessage, bankTurn, sendGuard]);

  /**
   * Idle timeout. Reported as its own state rather than an ordinary error: the
   * session is unrecoverable, so the composer locks and the only way forward is
   * a new session. The server mirrors this onto tse:error for older clients,
   * which is why onError drops timeout payloads instead of double-reporting.
   */
  const onSessionExpired = useCallback((payload) => {
    setSessionExpired(true);
    sessionLockedRef.current = true;
    // An expired id is REFUSED by tse:join, so no tse:joined will ever arrive to
    // clear the reconnect timer. Without this the session sits in `reconnecting`
    // forever behind the expiry banner, and the timer later fires into a locked
    // session.
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    setReconnecting(false);
    endSession("timeout");
    pendingQuestionSnapshotRef.current = null;
    setCurrentQuestion(null);
    setIsStreaming(false);
    streamingMsgIdRef.current = null;
    appendMessage({
      type: "error",
      text: payload?.message || "Session timed out. Start a new session to continue.",
    });
  }, [appendMessage, endSession]);

  /**
   * The socket dropped: network blip, wifi handover, laptop sleep, server restart.
   *
   * This used to lock the session permanently, because a rejoin for an id the
   * server no longer holds mints a BLANK session under that id — the transcript
   * stays on screen with nothing behind it. But the server keeps sessions for 24h,
   * so the overwhelmingly common case is that the session is still there and only
   * the socket went away. Locking on every drop threw away a live session over a
   * lift ride, and engine.io gives up after ~45s of client silence, so it fired
   * far more often than "the session is gone" actually happened.
   *
   * So: reconnect optimistically and let the SERVER adjudicate. onJoined receives
   * `resumed` and locks only when the session is genuinely gone. The composer stays
   * disabled meanwhile — a message typed now would go into a socket that is no
   * longer there.
   */
  // Declare the session lost. Shared by both grace timers so the teardown cannot
  // drift between them.
  const lockAsDisconnected = useCallback(() => {
    if (sessionLockedRef.current) return;
    sessionLockedRef.current = true;
    setReconnecting(false);
    setSessionExpired(true);
    endSession("disconnect");
    pendingQuestionSnapshotRef.current = null;
    setCurrentQuestion(null);
  }, [endSession]);

  /**
   * Arm the grace timer, never past the absolute deadline.
   *
   * Each arm is relative, so a connect/disconnect flap would otherwise reset the
   * window on every cycle and the session could stay "reconnecting" — composer
   * disabled, never locking — indefinitely. `disconnectedSinceRef` is set once on the
   * first drop and only cleared on a successful join, so the wall-clock cap holds
   * however many times the socket bounces.
   */
  const armReconnectTimer = useCallback((ms) => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    const since = disconnectedSinceRef.current;
    const remaining = since === null ? ms : RECONNECT_GRACE_MS - (Date.now() - since);
    if (remaining <= 0) {
      reconnectTimerRef.current = null;
      lockAsDisconnected();
      return;
    }
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      lockAsDisconnected();
    }, Math.min(ms, remaining));
  }, [lockAsDisconnected]);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    disconnectedSinceRef.current = null;
    // A join landed, so the outage is over — including for the auth-denial retry
    // budget. Without this a denial minutes later could inherit an exhausted
    // budget from an incident that has already been repaired.
    authRetryRef.current = { count: 0, startedAt: 0 };
  }, []);

  const onDisconnected = useCallback((reason) => {
    // Replies to what was on the wire (a command, a re-join) are lost with the transport.
    commandBusy.release();
    inFlightRejoin.reset();
    sessionLearning.abandon();
    // Our own teardown — unmount, or a session switch. Not a lost session.
    if (reason === "io client disconnect") return;
    // A DA narration bubble never stays streaming over a lost transport (whole sentences kept);
    // the rejoin's replay repaints what the server recorded.
    finalizeNarration();
    if (sessionLockedRef.current || !hasTurnsRef.current) return;
    // Stamped on the FIRST drop only — a flap must not extend the deadline.
    if (disconnectedSinceRef.current === null) disconnectedSinceRef.current = Date.now();
    setReconnecting(true);
    // The spinner belongs to a turn whose transport is gone, so stop it — but KEEP
    // streamingMsgIdRef. The server turn is still running and rebinds its emits to the
    // new socket, so chunks arriving after the rejoin must land in the SAME bubble.
    // Clearing the id sent them down the "start a new message" branch and split one
    // answer across two bubbles.
    setIsStreaming(false);
    armReconnectTimer(RECONNECT_GRACE_MS);
  }, [armReconnectTimer, finalizeNarration, commandBusy, inFlightRejoin, sessionLearning]);

  // Transport restored. The long grace window was covering the connect attempt; from
  // here only the join ack is outstanding, which is fast — so re-arm short rather than
  // leaving the session at risk for the remainder of the original window.
  const onConnected = useCallback(() => {
    if (sessionLockedRef.current || !reconnectTimerRef.current) return;
    armReconnectTimer(JOIN_ACK_GRACE_MS);
  }, [armReconnectTimer]);

  /**
   * Join acknowledged. `resumed: false` means the server built a fresh session under
   * this id — it has no memory of ours, so the transcript on screen is backed by
   * nothing and the next message would be treated as turn one. That is the case that
   * must lock. `resumed: true` means the session survived; carry on.
   *
   * A server predating the field sends no `resumed`; treated as resumed, so an older
   * backend keeps working instead of locking every session on reconnect.
   */
  const onJoined = useCallback((payload) => {
    // Ignore an ack for a session we are no longer on — the socket is shared, and a
    // late ack for a previous id must not clear the current session's timer.
    const acked = String(payload?.session_id || "");
    if (acked && sessionIdRef.current && acked !== sessionIdRef.current) return;
    // …and the id comparison above cannot catch the close-and-continue race:
    // handleClear re-joins the OLD session, so this ack can arrive while
    // sessionIdRef still names it (see clearInFlightRef). Everything this
    // handler restores FROM the payload is then the retired session's state,
    // landing on the successor — the parent's turn count in the child's
    // header, the parent's "closed" locking the child's composer. Gate the
    // seeding only: the connection bookkeeping below (reconnect timer,
    // turn_in_flight, resumed===false) is about the socket, not the session's
    // contents, and must still run.
    const staleAck = clearInFlightRef.current;
    clearReconnectTimer();
    if (!staleAck) sessionLearning.seedFromJoin(payload);
    // Names the conversation in the loader, so the wait says WHICH chat is
    // opening. Optional on the wire — older servers send neither field.
    const title = typeof payload?.title === "string" ? payload.title.trim() : "";
    if (title) setHydratingTitle(title);
    // Nothing stored under this id: no tse:replay is coming, so waiting for one
    // would hold the skeleton until the timeout for a session that is simply
    // empty. Only an explicit 0 counts — a server that omits the field leaves
    // the loader to the events below.
    const turnCount = payload?.turn_count;
    if (turnCount === 0 || turnCount === "0") endHydrate();
    // ── Implicit-search cadence survives a refresh ────────────────────────
    // The background cases/bugs refresh fires from bankTurn at turns 3, 6, 9…
    // — counted by copilotTurnRef, which is a ref and dies with the mount. A
    // resumed 7-turn session restarted the count at 0, so the panel needed
    // three MORE in-page turns before anything fired; refresh often enough and
    // it never fires at all (a full day's server log: zero implicit searches).
    // Seed the counter from the server's stored height so the cadence
    // continues instead of restarting. max(): a join ack must never move the
    // count backwards mid-session.
    // NaN under staleAck: every Number.isFinite() branch below (counter seed,
    // header turn count, catch-up search) falls through, which is exactly the
    // "do not seed from a retired session" behaviour wanted here.
    const seededTurns = staleAck ? NaN : Number(turnCount);
    // ── Live per-turn cost baseline survives a refresh / reconnect ─────────
    // bankTurn prices a turn as (session total now − total at the last bank).
    // Both refs die with the mount, so after a reload they restarted at 0 while
    // the server's running total did not — the first live turn was then billed
    // the WHOLE session so far (turns 1-3 again on top of turn 4). Re-seed both
    // from the server's total as of this join. Skipped mid-turn: re-baselining
    // then would drop the spend of the turn already being banked.
    // The baseline is the total as of the LAST COMMITTED turn (banked_cost_usd),
    // not the total at join: spend after that commit (background search, memory
    // upkeep) belongs to the next turn, exactly as the admin page counts it.
    // Older servers send only session_cost_usd — fall back to it.
    const joinedCost = staleAck ? NaN : Number(payload?.session_cost_usd);
    if (Number.isFinite(joinedCost) && joinedCost >= 0 && !turnPendingRef.current) {
      const bankedCost = Number(payload?.banked_cost_usd);
      const useBanked =
        payload?.banked_cost_usd != null && Number.isFinite(bankedCost) && bankedCost >= 0 && bankedCost <= joinedCost;
      const joinedSeq = payload?.cost_seq ?? null;
      seedCostLedger(costLedgerRef.current, {
        cost: joinedCost,
        bankedCost: useBanked ? bankedCost : joinedCost,
        seq: joinedSeq,
        bankedSeq: useBanked && payload?.banked_cost_seq != null ? payload.banked_cost_seq : joinedSeq,
      });
      absorbCostPayload(costLedgerRef.current, payload);
    }
    if (Number.isFinite(seededTurns) && seededTurns > copilotTurnRef.current) {
      copilotTurnRef.current = seededTurns;
    }
    // The Session Context drawer's turn count is mount-local too, so a resumed
    // 7-turn session opened as "Turns 0" and only started counting from the
    // next message. Same server-stored height, same monotonic guard: a
    // reconnect ack must not walk the number backwards mid-session. Count only:
    // the per-turn cost and latency are rebuilt from the stored envelopes in
    // onReplay, which may land either side of this ack — spreading `prev` here
    // leaves whatever it rebuilt (or will rebuild) untouched.
    if (Number.isFinite(seededTurns) && seededTurns > 0) {
      setSessionStats((prev) => (prev.turns >= seededTurns ? prev : { ...prev, turns: seededTurns }));
    }
    // Catch-up: a session past the first cadence mark whose panel never got a
    // background fill (the counter-reset bug ate every scheduled one) fills it
    // now. Once per mount — the ack re-arrives on every reconnect, and the
    // server's own novelty gate turns the redundant case into a cheap no-op
    // (reuse: results already delivered, nothing re-emitted).
    if (
      Number.isFinite(seededTurns) && seededTurns >= 3 &&
      !implicitCatchupFiredRef.current && payload?.resumed !== false &&
      !(payload?.escalation_active && variant === "enterprise")
    ) {
      implicitCatchupFiredRef.current = true;
      implicitSearchRef.current?.("both", 5);
    }
    // Observability: surface the storage tier this session was hydrated from.
    if (payload?.read_src) {
      setSessionSource({
        read_src: payload.read_src,
        read_ms: payload.read_ms,
        hydrated: payload.hydrated,
        from_ram: payload.from_ram,
      });
    }
    // ── Resumed-session state that only the join ack can restore ──────────
    // All three fields are additive on the wire; a server predating them sends
    // none, and every branch below is a no-op in that case.
    //
    // Guided mode is a session property, not a turn property: tse:mode only
    // fires when the mode CHANGES, so a resumed guided session would otherwise
    // come back in "fast" and lose the guided bar and Exit guided control.
    //
    // NOT gated on variant. It used to also require variant === "enterprise",
    // but only ChatWindow passes that — the engineer route leaves the prop at
    // its "default" value, so this branch resolved to "fast" for the one
    // surface guided mode is built for. A reload mid-walkthrough dropped the
    // Exit guided control.
    // Matches onMode below, which has never had a variant gate.
    if (!staleAck && payload?.mode) {
      setCopilotMode(payload.mode === "guided" ? "guided" : "fast");
    }
    // The product is re-announced on the join ack (nothing else re-emits it
    // on resume); the source-map link reads it from productRef.
    if (!staleAck && payload?.product) {
      productRef.current = payload.product;
    }
    if (!staleAck && payload?.product_change) {
      productChangeRef.current = payload.product_change;
      setProductLocked(!payload.product_change.can_change);
    }
    // A (re)join answers nothing a change request was waiting on — an answer
    // sent while this socket was away is lost — so the control is released and
    // the ack above says whether it may be used again.
    if (!staleAck) setChangePending(null);
    // handleAction gates EVERY action button on firstPromptRef ("Ask a question
    // first"), and nothing else restores it after a resume — so on a resumed
    // session Find Bugs / Find Cases / Next step / Possible causes / Diag map
    // all bounced even though the session plainly had turns.
    if (!staleAck && payload?.first_prompt && !firstPromptRef.current) {
      firstPromptRef.current = payload.first_prompt;
    }
    if (!staleAck && payload?.escalation_active === false && variant === "enterprise") {
      // The record says the live chat is over. Previously this branch did not
      // exist — the flag was only ever acted on when TRUE — so a chat closed
      // while the tab was away (reconnect timeout, the engineer ending it,
      // anything out of band) left the customer looking at a page that still
      // claimed an agent was with them.
      updateEscalationActive(false);
      if (payload?.live_chat_state === "ended") {
        setAgentJoinedNotice("You're back with the AI assistant.");
      }
    }
    if (!staleAck && payload?.escalation_active) {
      updateEscalationActive(true);
      // The "being escalated" line is a streamed chunk, not a turn, so it is
      // not in storage and no replay brings it back — the customer refreshed
      // and lost every sign that they had asked for a human. The flag IS
      // restored on the ack, so the notice is rebuilt from it.
      //
      // Persistent, with no auto-clear: it describes the state the session is
      // in, not something that just happened.
      if (variant === "enterprise") {
        // The name is read from storage, not from the `liveAgentName` state:
        // this callback is memoised and would close over the value the ack
        // arrived with, which on a fresh mount is empty.
        const agentSoFar = (localStorage.getItem("TseAgentName") || "").trim();
        const waiting = payload?.live_chat_state === "waiting";
        setAgentJoinedNotice((prev) => prev || (
          !waiting && agentSoFar
            ? `You're connected to ${agentSoFar}.`
            : "You've been escalated to a live support agent. Please hold."
        ));
      }
    }
    // ── Restore call context from the ack ────────────────────────────────
    // Seeded only when this mount has nothing of its own. The ack re-arrives on
    // every reconnect and can predate blocks added since — overwriting them
    // would walk the drawer backwards.
    if (
      !staleAck && typeof payload?.call_context === "string" &&
      callContextBlocksRef.current.length === 0
    ) {
      const blocks = parseCallContextBlocks(payload.call_context);
      if (blocks.length) {
        setCallContextBlocks(blocks);
        const cards = callContextCards(blocks);
        // Prepended: this is what the agent was told BEFORE the turns
        // onReplay is about to paint. De-duped by id so a reconnect ack
        // landing after onReplay cannot double the cards.
        setMessages((prev) => {
          const have = new Set(prev.map((m) => m.id));
          const missing = cards.filter((c) => !have.has(c.id));
          return missing.length ? [...missing, ...prev] : prev;
        });
      }
    }
    // A closed diagnostic cannot take another message — the server revives it
    // behind the scenes if one arrives, which is not what the engineer meant by
    // closing it. Nothing else told the client: tse:feedback_prompt fires once,
    // on the LIVE close, so a session reopened from Recents came back with an
    // unlocked composer and (before _resume_client learned to check) the close
    // gate still on screen offering to close what was already closed.
    if (!staleAck && payload?.closed) {
      setSessionClosed(true);
      setCurrentQuestion(null);
      setIsStreaming(false);
      resumedClosedRef.current = true;
    }
    // When the server says no turn is running but this tab still shows the
    // spinner, the answer was delivered to another tab (or the turn finished
    // while we were disconnected). Clear the streaming state exactly as onIdle
    // does — but only if the turn wasn't started locally very recently (the
    // server may not have received it yet on initial join).
    if (payload?.turn_in_flight === false && isStreamingRef.current) {
      const localTurnAge = turnStartRef.current ? Date.now() - turnStartRef.current : Infinity;
      if (localTurnAge > 5000) {
        streamingMsgIdRef.current = null;
        setIsStreaming(false);
        turnPendingRef.current = false;
        turnStartRef.current = 0;
      }
    }
    // The opposite case: a turn is still running (a reload, or switching back to
    // this session mid-answer) and this tab shows nothing of it. Thinking until its
    // terminator; the finished turn is then re-hydrated (useInFlightRejoin).
    if (
      !staleAck && !payload?.closed && payload?.resumed !== false &&
      inFlightRejoin.noteJoin(payload, { streaming: isStreamingRef.current })
    ) {
      setIsStreaming(true);
      turnPendingRef.current = true;
      turnStartRef.current = Date.now();
    }
    if (payload?.resumed === false && hasTurnsRef.current) {
      // The session is gone; the banner takes over the panel, so the loader
      // must not sit behind it waiting for a replay that cannot come.
      endHydrate();
      lockAsDisconnected();
      return;
    }
    // A join LANDED and the server resumed the session — so "Connection lost.
    // Your conversation is still saved" is now describing an outage that is
    // over, above a transcript the server just re-sent.
    //
    // It could stick because the lock is one-way: the grace timer fires
    // `lockAsDisconnected()` while a join is still in flight, the ack arrives a
    // moment later, and nothing here ever cleared it — leaving a live session
    // behind a banner whose only offered remedy is starting a new one. The
    // engineer's copilot rarely shows this because its id is in the URL from
    // mount, so its joins ack promptly; a lazily-minted enterprise session has
    // a wider window for the timer to win that race.
    //
    // Safe precisely because the SERVER has already adjudicated. This is not
    // the speculative rejoin `handleResumeSession` guards against — that one
    // re-opens the socket blind and can mint a blank session behind the
    // transcript. Here the answer is already in hand: resumed !== false means
    // the conversation is real and its turns have been replayed.
    if (sessionLockedRef.current && sessionEndReasonRef.current === "disconnect") {
      sessionLockedRef.current = false;
      setSessionExpired(false);
      endSession(null);
      // `resumePending` is not cleared here: the effect on `reconnecting`
      // already does it, and setResumePending is declared further down this
      // component — naming it here is a temporal dead zone, not a no-op.
    }
    setReconnecting(false);
  }, [clearReconnectTimer, lockAsDisconnected, endHydrate, setCallContextBlocks, endSession, setChangePending, inFlightRejoin, sessionLearning]);

  const shouldRejoin = useCallback(() => !sessionLockedRef.current, []);

  // Never let the grace timer outlive the component; firing after unmount would set
  // state on a dead tree.
  useEffect(() => () => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
  }, []);

  /**
   * The server refused this session — for one of two very different reasons.
   *
   * `reason: "auth_required" | "auth_unavailable"` means the socket has no usable
   * identity (an expired token at handshake time, or an identity the server could
   * not resolve). The session is very probably OURS; the connection just cannot
   * prove it. Treat it exactly like a dropped transport: keep the transcript, show
   * reconnecting, refresh the token and reconnect. Wiping here is what threw an
   * engineer mid-call onto a blank chat, because only a SUCCESSFUL join can restore
   * a transcript and the join is the thing that just failed.
   *
   * Anything else — including a payload with no `reason` at all, which is what an
   * older server sends — is `not_session_owner` and keeps the original behaviour
   * below.
   *
   * A rejoin on the same socket cannot help: the server fixes the principal at
   * connect time, so the reconnect in reauthenticate() is the only repair.
   */
  const onAccessDeniedRetryable = useCallback(() => {
    // The retry budget is per burst: a denial arriving long after the last one is
    // a new incident and starts a fresh window.
    const budget = authRetryRef.current;
    const now = Date.now();
    if (!budget.startedAt || now - budget.startedAt > AUTH_RETRY_WINDOW_MS) {
      budget.startedAt = now;
      budget.count = 0;
    }
    // Same posture as a dropped socket: the transcript stays, the composer waits,
    // and the existing grace timer is what eventually declares the session lost.
    // streamingMsgIdRef is deliberately kept — a turn still running server-side
    // rebinds its emits to the reconnected socket and must land in the same bubble.
    if (disconnectedSinceRef.current === null) disconnectedSinceRef.current = Date.now();
    setIsStreaming(false);
    setReconnecting(true);
    armReconnectTimer(RECONNECT_GRACE_MS);
    // Out of budget: stay in the reconnecting state and let the grace timer reach
    // lockAsDisconnected() rather than spin.
    if (budget.count >= AUTH_RETRY_LIMIT) return;
    budget.count += 1;
    Promise.resolve(reauthenticateRef.current?.()).then((result) => {
      // Only a rejected credential is terminal. A network failure is
      // indistinguishable from the outage we are already sitting in, so it stays
      // in the reconnecting state — logging out on a dropped packet is the one
      // outcome worse than the bug being fixed here.
      if (result?.error !== "unauthorized") return;
      if (embedded) {
        setAccessDeniedToast(true);
        return;
      }
      // returnTo so signing back in can come straight back to this session
      // instead of a blank one; the transcript is safe server-side.
      navigate("/", {
        replace: true,
        state: { authExpired: true, returnTo: location.pathname + location.search },
      });
    });
  }, [armReconnectTimer, embedded, navigate, location.pathname, location.search]);

  /**
   * The server refused this session: it belongs to another user.
   *
   * A session id IS the URL, so this is what a pasted /TseDashboard5/<id> link
   * hits in a second logged-in profile. Not an error banner and not a lock —
   * both would leave the visitor parked on an address that is not theirs, with
   * whatever the panel had already drawn still on screen. Wipe the transcript,
   * leave for a brand-new session, and say why on arrival.
   *
   * The toast rides on the navigation state because the redirect remounts this
   * component (App.js keys it on the route's session id), so any state set here
   * dies with the old instance.
   */
  const onAccessDenied = useCallback((payload) => {
    const reason = payload?.reason;
    if (reason === "auth_required" || reason === "auth_unavailable") {
      onAccessDeniedRetryable();
      return;
    }
    endHydrate();
    sessionLockedRef.current = true;
    streamingMsgIdRef.current = null;
    setIsStreaming(false);
    setCurrentQuestion(null);
    setMessages([]);
    if (embedded) {
      // No route to redirect to. Showing the notice in place is the whole
      // remedy available here — and the transcript is already cleared above.
      setAccessDeniedToast(true);
      return;
    }
    navigate(NEW_SESSION_PATH, {
      replace: true,
      state: { accessDenied: true },
    });
  }, [embedded, endHydrate, navigate, onAccessDeniedRetryable]);

  const onError = useCallback((payload) => {
    // An error is an answer too: whatever the join was waiting for is not
    // coming, so stop showing the transcript skeleton over the top of it.
    endHydrate();
    if (payload?.reason === "timeout") return;
    appendMessage({ type: "error", text: payload?.message || "Something went wrong. Please try again." });
    streamingMsgIdRef.current = null;
    setIsStreaming(false);
    sendGuard.release();
    commandBusy.release();
    inFlightRejoin.noteTurnEnd();
    // A pending product change will not be answered after an error.
    setChangePending(null);
  }, [appendMessage, endHydrate, setChangePending, sendGuard, commandBusy, inFlightRejoin]);

  // The server accepted a product change (tse:product_change_accepted, sent to
  // the whole room). By the time this arrives the wrong-product run has been
  // stopped and joined server-side, storage and memory are reset, and the
  // re-run has NOT started — so this is the one safe moment to wipe, and every
  // tab on the session does it, not just the one that clicked.
  //
  // What goes: the latest "Product identified" banner and everything after it
  // (the wrong-product answer, its cards, the map, the stats). What stays: the
  // engineer's prompt above it. The re-run brings its own banner.
  const onProductChangeAccepted = useCallback((payload) => {
    const sid = String(payload?.session_id || "");
    if (sid && sessionIdRef.current && sid !== sessionIdRef.current) return;
    setChangePending(null);
    setMessages((prev) => {
      let idx = -1;
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i].type === "product") { idx = i; break; }
      }
      const kept = idx < 0 ? prev : prev.slice(0, idx);
      return kept.filter((m) => !isTransientCopilotRow(m));
    });
    streamingMsgIdRef.current = null;
    streamRawRef.current = "";
    closeIntentRef.current = null;
    latestBreakdownRef.current = null;
    pendingQuestionSnapshotRef.current = null;
    // The replacement session starts its cost from zero.
    costLedgerRef.current = createCostLedger();
    copilotTurnRef.current = 0;
    setSessionStats({ turns: 0, totalCostUsd: 0, totalLatencyMs: 0, turnLog: [] });
    setCurrentQuestion(null);
    setCopilotMode("fast");
    rememberSessionMode("fast");
    setCtxResetKey((k) => k + 1);
    sidebar.setBugs([]);
    sidebar.setCases([]);
    setResultsMeta({ bugs: {}, cases: {} });
    if (payload?.product) productRef.current = payload.product;
    turnStartRef.current = Date.now();
    turnPendingRef.current = true;
    setIsStreaming(true);
  }, [setChangePending, sidebar]);

  // The server refused the change (tse:product_change_refused, this socket
  // only). Nothing was wiped on the click, so nothing is restored: the pending
  // control is released and the reason is shown. Deliberately NOT routed
  // through onError — that ends the streaming state, and a refusal can leave
  // the answer the engineer is reading still streaming.
  const onProductChangeRefused = useCallback((payload) => {
    const sid = String(payload?.session_id || "");
    if (sid && sessionIdRef.current && sid !== sessionIdRef.current) return;
    setChangePending(null);
    if (payload?.message) appendMessage({ type: "error", text: payload.message });
  }, [appendMessage, setChangePending]);

  /**
   * The agent run ended without asking anything back. tse:done can't say this
   * on its own — inform_user reports turn_complete: false because the agent
   * usually has another round-trip to make — so on the turns where the LLM
   * just answers and stops, this is the only thing that releases the composer.
   */
  const onIdle = useCallback((payload) => {
    // sockets_tse fires this on every join it had to build a session for, which
    // is exactly the "nothing to replay" case — and on a real turn end it means
    // the transcript is already on screen. Either way the loader is done.
    endHydrate();
    // The join's own composer release, sent while the turn the ack reported is
    // still running: the thinking row stays up for that turn's real terminator.
    if (inFlightRejoin.consumeJoinIdle(payload)) return;
    streamingMsgIdRef.current = null;
    setIsStreaming(false);
    sendGuard.release();
    commandBusy.release();
    inFlightRejoin.noteTurnEnd();
    const isFiniteCost = absorbCostPayload(costLedgerRef.current, payload);
    // V5 turn end: the turn's own breakdown (DA narrator, orchestrator, engine reader, embeddings ...).
    if (isFiniteCost && payload?.breakdown?.length) {
      latestBreakdownRef.current = payload.breakdown;
    }
    // tse:idle is NOT turn-scoped — sockets_tse.py also fires it, with only
    // {session_id}, on every tse:join that has to build a fresh session (first
    // visit, or a reconnect after the server lost the old one). session_cost_usd
    // is only ever present on the real turn-end emit (on_turn_idle), so
    // isFiniteCost is what tells a genuine "this turn asked nothing new" apart
    // from "you just (re)connected" — restoring on the latter would put a
    // stale card in front of a session that never asked it.
    //
    // Functional update, not a ref read: onQuestion → onIdle can fire back to
    // back within one socket.io dispatch, before this component re-renders, so
    // a ref mirroring currentQuestion would still read the PRE-onQuestion
    // value. `prev` here is always current at the moment React applies it.
    if (isFiniteCost && pendingQuestionSnapshotRef.current) {
      const pending = pendingQuestionSnapshotRef.current;
      setCurrentQuestion((prev) => prev ?? pending);
    }
    pendingQuestionSnapshotRef.current = null;
    if (!payload?.source_replacement) enableLatestSourceReplacement();
    // Traversal is deliberately not re-applied here: tse:done already fed this
    // turn's nodes to the map, and applying twice would double the trail.
    bankTurn();
  }, [bankTurn, endHydrate, enableLatestSourceReplacement, inFlightRejoin, sendGuard, commandBusy]);

  const onMode = useCallback((payload) => {
    const mode = payload?.mode === "guided" ? "guided" : "fast";
    setCopilotMode(mode);
    // Persisted so a reload knows whether this session is worth rebinding to:
    // a walkthrough is, a hybrid thread is not.
    rememberSessionMode(mode);
    if (mode === "fast") {
      pendingQuestionSnapshotRef.current = null;
      setCurrentQuestion(null);
      setIsStreaming(false);
    }
  }, []);

  // Session closed. Leave step-by-step guided mode (bar + Exit guided) — diagnosis
  // is finished; staying "guided" after close is a stale UI state. Also clear any
  // live question card: the agent loop has ended, so answering would hit a dead session.
  const onFeedbackPrompt = useCallback((payload) => {
    setCopilotMode("fast");
    setCurrentQuestion(null);
    setIsStreaming(false);
    appendMessage({ type: "feedback_prompt", text: payload?.text || "This session has been closed — please share your feedback." });
    // When the close followed a context-switch gate ("close case" with a
    // deferred utterance), show the banner that seeds the next session with it.
    // When the close came from clicking "Yes, close session" there is no
    // deferred query — the feedback row alone is sufficient; the bare "This
    // session is closed" banner added nothing and was removed intentionally.
    const deferredQuery = contextSwitchQueryRef.current || "";
    if (deferredQuery) {
      appendMessage({ type: "context_switch_resume", query: deferredQuery });
    }
    // Locks the composer. The old session cannot be continued from here; the
    // banner's button (or /clear) is the way forward.
    setSessionClosed(true);
    // Mirrors setSessionClosed above but for the sidebar: /api/copilot/sessions
    // reads Postgres, which lags Redis through the write-behind outbox, so a
    // forced refetch right now would still say this session is open. Marking
    // it locally is the only way Recents shows "Closed" before that flush lands.
    if (hasRecentsRail && sessionIdRef.current) {
      window.dispatchEvent(new CustomEvent(RECENTS_CLOSED_EVENT, {
        detail: { sessionId: sessionIdRef.current },
      }));
    }
  }, [appendMessage, hasRecentsRail]);

  const onNodeVisited = useCallback(() => {}, []);

  // ── Phase 5: replay hydrated turns into the transcript ──────────────────
  //
  // `turn.blocks` is what the client actually SAW, in emit order (recorded by
  // _record_block server-side). Walking it is the only way a resumed session
  // renders the same transcript as the live one: status lines, the product
  // chip, source cards and the answer interleave differently per mode, so
  // rebuilding from the flat semantic fields would have to guess an order.
  // Turns written before blocks existed have none — those still take the old
  // user/assistant path below.
  const onReplay = useCallback((payload) => {
    // Same guard, and the same reason, as onJoined's ack and onUserMessage's
    // echo: the socket outlives the session id. Close-and-continue re-joins the
    // CLOSED session first (handleClear unlocks and re-joins before it asks the
    // server to clear), so the parent's replay is already in flight when the
    // successor arrives — and landing it here would repaint the closed
    // diagnostic's whole transcript on top of the fresh session's one bubble,
    // under the successor's URL.
    const replayedSession = String(payload?.session_id || "");
    if (replayedSession && sessionIdRef.current && replayedSession !== sessionIdRef.current) return;
    // The guard above only closes ONE of the two orderings: it works when
    // tse:cleared lands first (sessionIdRef already the successor), and passes
    // straight through when the parent's replay lands first (sessionIdRef still
    // the parent, so the ids match). See clearInFlightRef for the full account.
    // While a clear is in flight, drop the payload whatever session it names —
    // the parent's transcript is being discarded and the successor's replay
    // comes from the join onCleared issues after the flag is down. The id
    // comparison stays as well: it still covers a late replay once the flag is
    // clear.
    if (clearInFlightRef.current) return;
    // The transcript has arrived (or the server says there is none) — either
    // way the loader's job is over. Before the walk below, so an empty/dropped
    // payload still releases it.
    endHydrate();
    // A re-hydrate read storage before the finished turn's commit landed: the
    // REPLACE would drop the answer on screen. Kept; the hook retries the join.
    if (!inFlightRejoin.acceptReplay(payload)) return;
    sessionLearning.seedFromReplay(payload);

    const turns = payload?.turns;

    // A closed session with zero replayable turns still needs the feedback row
    // (and optionally the context-switch banner), so we no longer bail out
    // early when resumedClosedRef is set — the push block below handles it.
    // `live_chat` is the human half of an escalated conversation. It is not in
    // `turns` and never will be — once an agent takes over, no turn is
    // committed — so a session can legitimately have messages to repaint with
    // an empty turn list, and bailing here used to drop them.
    const liveChat = Array.isArray(payload?.live_chat) ? payload.live_chat : [];
    if (
      (!Array.isArray(turns) || turns.length === 0) &&
      liveChat.length === 0 &&
      !resumedClosedRef.current
    ) return;
    const replayed = [];
    // Carried across turns: the question the PREVIOUS turn ended on. Live, a
    // reply that matches one of its options is frozen as an answered_question
    // card *instead of* a user bubble (see handleSend / handleAnswer), so
    // replay has to make the same call to land on the same transcript.
    let prevQuestion = null;
    // Rebuilt alongside the transcript: the header's Turn breakdown was
    // mount-local, so a resumed session showed the right turn count over an
    // empty table. The server now stores `metrics` (latency_ms/cost_usd) per
    // envelope. `steps` stays empty — the per-step breakdown is part of the
    // traversal payload the server deliberately does not persist (see the NOTE
    // in _commit_turn), and CopilotPanel gates the row's drill-down on
    // steps.length, so a replayed row renders in full and just does not expand.
    const replayLog = [];
    const finite = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    // The replay REPLACES the transcript, so the replaceable turn is decided
    // again from it below — never inherited from what was on screen before.
    activeSourceTurnRef.current = null;
    // The latest hybrid turn's Replace candidates. Stored blocks no longer carry
    // them; the server sends the current turn's from session state, keyed by
    // its token, so they can only land on that turn's cards.
    const replayBench = payload?.source_alternatives || {};
    const benchFor = (turnId, stored) => (
      turnId && replayBench.turn_id === turnId && Array.isArray(replayBench.alternatives)
        ? replayBench.alternatives
        : (Array.isArray(stored) ? stored : [])
    );

    (Array.isArray(turns) ? turns : []).forEach((turn, ti) => {
      if (!turn || typeof turn !== "object") return;
      const n = turn.n ?? ti;
      const isLatestTurn = ti === (Array.isArray(turns) ? turns.length : 0) - 1;
      // Where this turn's rows start, so every one of them can be stamped with
      // the SERVER's turn number below. setMessages() at the end of a replay is
      // a REPLACE and bypasses appendMessage, so without this pass a replayed
      // transcript carries no turnNo at all and the learning overlay falls back
      // to counting messages — which drifts by one for every stored turn with
      // no user message, and files corrections against the wrong answer.
      const from = replayed.length;
      // Envelopes stored before `metrics` existed have none; they yield a row
      // of zeros, which the renderer prints as "—" rather than a made-up
      // number. Coerced through Number.isFinite so a malformed value cannot
      // reach toFixed() and paint "NaN".
      const m = turn.metrics || {};
      // `metrics.cache_hit` does not exist on the wire — _commit_turn persists
      // only {latency_ms, cost_usd} there (server/app/sockets_tse.py), so
      // `m.cache_hit` is always undefined and this flag would always read
      // false. The turn's assistant block, however, IS stamped `cache_hit:
      // true` by the server's cache-repaint path (see the `b.cache_hit` read
      // below), so that is rehydrated onto this entry once the blocks are
      // walked — replayEntry stays a live reference into replayLog for that.
      const replayEntry = {
        n,
        latencyMs: Math.max(0, finite(m.latency_ms)),
        costUsd: Math.max(0, finite(m.cost_usd)),
        cacheHit: !!m.cache_hit,
        steps: [],
      };
      replayLog.push(replayEntry);
      // The server writes ts as epoch SECONDS (int(time.time())), but JS
      // Date expects milliseconds. Stored sessions already have seconds in
      // Redis/Postgres, so we normalise client-side: <1e12 is seconds, >=1e12
      // is already milliseconds, and ISO strings fall through to Date parse.
      const raw = turn.ts;
      const tsNum = typeof raw === "number" ? raw : Number(raw);
      const ts = Number.isFinite(tsNum)
        ? (tsNum > 1e12 ? tsNum : tsNum * 1000)
        : (raw ? new Date(raw).getTime() : Date.now());

      // A DA card answered in this turn: rebuilt from the recorded da_question /
      // da_answer blocks so the answered card keeps every option (batch included).
      const daRow = turn.user && prevQuestion?.daCard
        ? replayDaAnswer(prevQuestion.daCard, (Array.isArray(turn.blocks) ? turn.blocks : []).find((b) => b?.t === DA_ANSWER_BLOCK), turn.user)
        : null;
      if (daRow) {
        replayed.push({ id: `replay-u-${n}`, ...daRow, askedAt: prevQuestion.askedAt, timestamp: ts });
      } else if (turn.user) {
        const lower = String(turn.user).trim().toLowerCase();
        const matched = prevQuestion && !prevQuestion.followups && prevQuestion.options.length
          ? prevQuestion.options.find((opt) => String(opt).trim().toLowerCase() === lower)
          : null;
        if (matched) {
          replayed.push({
            id: `replay-u-${n}`,
            type: "answered_question",
            sender: "agent",
            text: matched,
            questionSnapshot: { question: prevQuestion.text, options: prevQuestion.options },
            selectedAnswer: matched,
            askedAt: prevQuestion.askedAt,
            timestamp: ts,
          });
        } else {
          if (prevQuestion?.followups) {
            // Answering consumes the suggestion bubbles, exactly as clicking or
            // typing one does live.
            for (let k = replayed.length - 1; k >= 0; k -= 1) {
              if (replayed[k].type === "followups" && !replayed[k].answered) {
                replayed[k] = { ...replayed[k], answered: true, selected: turn.user };
                break;
              }
            }
          }
          replayed.push({
            id: `replay-u-${n}`,
            sender: "agent",
            text: turn.user,
            timestamp: ts,
          });
        }
      }
      prevQuestion = null;

      const blocks = Array.isArray(turn.blocks) ? turn.blocks : null;
      if (blocks && blocks.length) {
        // Enriched cards land on the answer bubble live (onSourcesEnriched
        // patches the streaming message), so they are held until the assistant
        // block they belong to and only stand alone if none follows.
        let pendingSources = null;
        let sawAssistant = false;
        blocks.forEach((b, i) => {
          const id = `replay-b-${n}-${i}`;
          switch (b?.t) {
            case "learning_note":
              if (b.text && variant !== "enterprise") {
                replayed.push({ id, type: "learning_note", text: b.text, enabled: b.enabled !== false, timestamp: ts });
              }
              break;
            case "status":
              // Same shape the live path builds in onInfo -- `query` and `tool`
              // are recorded on the block server-side and ride the envelope all
              // the way here, so replaying only `text` was throwing away the one
              // field that makes the "show query" disclosure work after a resume.
              if (b.text) {
                replayed.push({
                  id,
                  type: "status",
                  text: b.text,
                  query: b.query || "",
                  tool: b.tool || "",
                  timestamp: ts,
                });
              }
              break;
            case "product": {
              const change = productChangeRef.current;
              replayed.push({
                id,
                type: "product",
                text: b.text || `Product identified: ${b.product}`,
                product: b.product,
                ...(change?.can_change
                  ? { canChange: true, products: Array.isArray(change.products) ? change.products : [] }
                  : {}),
                timestamp: ts,
              });
              break;
            }
            case "sources":
              if (Array.isArray(b.items) && b.items.length) {
                pendingSources = {
                  items: b.items,
                  // The server's replay bench when it names this turn;
                  // `b.alternatives` exists only on envelopes stored by the
                  // first cut of this feature. Older turns get none — they can
                  // never be replaced.
                  alternatives: isLatestTurn ? benchFor(b.turn_id, b.alternatives) : [],
                  turnId: b.turn_id || "",
                  canReplace: !!b.can_replace,
                };
              }
              break;
            case "feedback_prompt":
              // Real closing banner, persisted server-side. Takes priority
              // over the resumedClosedRef synthetic fallback further down —
              // that fallback only fires when no such block was stored
              // (sessions closed before this was tracked).
              if (b.text) {
                replayed.push({ id, type: "feedback_prompt", text: b.text, timestamp: ts });
                resumedClosedRef.current = false;
              }
              break;
            case "learning_prompt":
              // V5 learning banner, stored in the same shape as the live
              // tse:learning_prompt row (tse_learning_prompt.schema.json).
              if (b.id && b.variant && b.text) {
                replayed.push({ ...b, type: "learning_prompt", timestamp: ts });
              }
              break;
            case "assistant": {
              const sourceMeta = pendingSources;
              const sources = (
                Array.isArray(b.sources) && b.sources.length
                  ? b.sources
                  : sourceMeta?.items
              ) || undefined;
              pendingSources = null;
              sawAssistant = true;
              const replayTurnId = b.turn_id || sourceMeta?.turnId || "";
              const replaceable = (
                isLatestTurn
                && turn.mode === "fast"
                && !turn.stopped
                && !!replayTurnId
                && !!sourceMeta?.canReplace
                && (sources?.length || 0) > 0
              );
              replayed.push({
                id,
                sender: "copilot",
                // Strip the close checkpoint from stored text: sessions
                // closed before the server-side fix still carry it raw.
                text: splitCloseIntent(b.text).text || "",
                sources,
                sourceTurnId: replayTurnId || undefined,
                sourceAlternatives: sourceMeta?.alternatives || [],
                canReplaceSource: replaceable,
                timestamp: ts,
                isDone: true,
                // Persisted by _repaint_guided_cache_blocks on cache-served turns.
                cacheHit: !!b.cache_hit,
              });
              // WHENEVER the turn is replaceable — not only when it has
              // alternatives. Cite needs none, and with the ref unset the
              // started/complete events of a Cite clicked after a reload were
              // dropped: a spinner that never cleared and the rewrite streaming
              // into a second bubble.
              if (replaceable) activeSourceTurnRef.current = replayTurnId;
              // The turn-log row has no cache flag of its own on the wire (see
              // the comment on replayEntry above) — carry it over from the
              // block so the turns-list "⚡" marker survives a resume/reload
              // too, not just the per-message badge.
              if (b.cache_hit) replayEntry.cacheHit = true;
              // Feed the trail in turn order so the map reads as the path the
              // session actually walked. Only guided turns advance the map.
              break;
            }
            case DA_NARRATION_BLOCK: {
              // V5 DA text as it was shown (sources above it), no streaming animation.
              const row = replayNarrationMessage(b, ts);
              if (row) replayed.push(row);
              break;
            }
            case DA_QUESTION_BLOCK: {
              // The DA card itself is not drawn (a pending one is re-sent live by
              // the revive); the next turn's answer freezes it, options and all.
              prevQuestion = {
                text: b.text || "",
                options: ((b.questions || [])[0]?.options || []).map((o) => o?.label).filter(Boolean),
                pending: true,
                followups: false,
                askedAt: ts,
                daCard: b,
              };
              break;
            }
            case "question": {
              const isFollowups = String(b.text || "") === FOLLOWUPS_MARKER;
              if (isFollowups) {
                replayed.push({
                  id,
                  type: "followups",
                  options: Array.isArray(b.options) ? b.options : [],
                  askedAt: ts,
                  timestamp: ts,
                });
              }
              // A still-pending blocking question is NOT drawn into the
              // transcript: _resume_client re-emits it as the live card above
              // the composer, which is where it was live too. One that was
              // answered is rendered by the next turn's user message above.
              prevQuestion = {
                text: b.text || "",
                options: Array.isArray(b.options) ? b.options : [],
                pending: !!b.pending,
                followups: isFollowups,
                askedAt: ts,
              };
              // Feed the trail so the map sees question-ending guided turns.
              // recordNode is idempotent, so if the assistant block already
              // called applyTurn for this nid the counter won't advance.
              break;
            }
            case "panel": {
              const items = b.items || {};
              if (b.kind === "cases") onCases(items);
              else if (b.kind === "bugs") onBugs(items);
              else if (b.kind === "case_analysis") {
                const analysis = items.analysis || items;
                if (analysis && Object.keys(analysis).length) {
                  replayed.push({
                    id,
                    type: "case_analysis",
                    sender: "copilot",
                    analysis,
                    isDone: true,
                    timestamp: ts,
                  });
                }
              }
              break;
            }
            default:
              break;
          }
        });
        if (pendingSources && !sawAssistant) {
          const replaceable = (
            isLatestTurn
            && turn.mode === "fast"
            && !!pendingSources.turnId
            && !!pendingSources.canReplace
            && (pendingSources.items?.length || 0) > 0
          );
          replayed.push({
            id: `replay-b-${n}-sources`,
            sender: "copilot",
            text: "",
            sources: pendingSources.items,
            sourceTurnId: pendingSources.turnId || undefined,
            sourceAlternatives: pendingSources.alternatives || [],
            canReplaceSource: replaceable,
            timestamp: ts,
            isDone: true,
          });
          // The sources-only branch offers Replace/Cite too, so it must arm the
          // token the started/complete events are matched against.
          if (replaceable) activeSourceTurnRef.current = pendingSources.turnId;
        }
      } else if (turn.assistant) {
        replayed.push({
          id: `replay-a-${n}`,
          sender: "copilot",
          // Block-less turns carry the raw checkpoint for the same reason.
          text: splitCloseIntent(turn.assistant).text,
          sources: turn.sources || undefined,
          timestamp: ts,
          isDone: true,
        });
      }

      for (let k = from; k < replayed.length; k += 1) {
        replayed[k] = { ...replayed[k], turnNo: n };
      }
    });

    // Diagnostic: the server log already proves tse:replay went out with N
    // turns, so if this line never appears the event is not reaching the
    // handler; if it appears with rendered=0 the walk dropped everything.
    // eslint-disable-next-line no-console
    console.log("[replay] turns=%d rendered=%d", turns?.length ?? 0, replayed.length);

    // Merge rather than overwrite, because tse:replay and tse:joined arrive in
    // no guaranteed order and a reconnect can replay a session whose later
    // turns were already banked live in this mount. A live row wins over its
    // replayed twin — it carries the same numbers plus the `steps` that make it
    // expandable — and a live row with no stored twin (a turn committed after
    // this replay was built) is kept instead of being dropped. Skipped entirely
    // when there is nothing to merge, so an empty replay on a resumed closed
    // session cannot zero out stats that are already correct.
    if (replayLog.length) {
      setSessionStats((prev) => {
        const merged = new Map(replayLog.map((t) => [t.n, t]));
        (prev.turnLog || []).forEach((t) => merged.set(t.n, t));
        const turnLog = [...merged.values()].sort((a, b) => a.n - b.n);
        return {
          // Monotonic, like onJoined's seed: neither event may walk the count
          // backwards, and the join ack can legitimately know of a turn whose
          // envelope this replay predates.
          turns: Math.max(prev.turns, turnLog.length),
          totalCostUsd: turnLog.reduce((sum, t) => sum + t.costUsd, 0),
          totalLatencyMs: turnLog.reduce((sum, t) => sum + t.latencyMs, 0),
          turnLog,
        };
      });
    }

    // Appended INSIDE the replayed list, not after it, for the reason given on
    // resumedClosedRef: this runs in the same setMessages that repaints the
    // transcript, so the banner cannot be replaced by it. It is the only way
    // out of a closed session — without it the composer is locked and the panel
    // offers nothing.
    // On a resumed closed session the feedback row MUST appear — it is the
    // only indication that the session ended and the only path to the feedback
    // modal. A context_switch_resume row is added only when there is a deferred
    // query to carry forward; the empty-query "This session is closed" variant
    // was removed because the feedback row already communicates that.
    if (resumedClosedRef.current) {
      resumedClosedRef.current = false;
      replayed.push({
        id: "replay-closed-feedback",
        type: "feedback_prompt",
        text: "This session has been closed — please share your feedback.",
        timestamp: Date.now(),
      });
    }

    // The live chat goes AFTER every replayed turn: a human only takes over
    // once the AI has stopped, so that is the true order. Shaped like the
    // bubbles `liveAgentMessage` pushes live, so a refreshed transcript and a
    // watched one render identically.
    liveChat.forEach((entry, i) => {
      const text = String(entry?.text || "").trim();
      if (!text) return;
      // The narration of what happened to the conversation — escalated, agent
      // joined, chat ended. Recorded beside the live chat because it is a
      // streamed chunk otherwise, and chunks are not persisted.
      if (entry.sender === "system") {
        replayed.push({
          id: `replay-sys-${i}`,
          type: "system_note",
          text,
          timestamp: entry.ts || Date.now(),
        });
        return;
      }
      const fromAgent = entry.sender === "agent";
      replayed.push({
        id: `replay-live-${i}`,
        text,
        // "live_agent", NOT "agent" — the exact sender onLiveAgentMessage uses
        // (:2278). CopilotPanel treats BOTH "user" and "agent" as "the person
        // holding this dashboard" and renders them right-aligned as "You"
        // (CopilotPanel.jsx:1744), so replaying the engineer's message as
        // "agent" put it on the customer's own side, over the customer's name.
        // A replayed bubble has to carry the sender the live one carries, or
        // the transcript changes sides on refresh.
        sender: fromAgent
          ? (variant === "enterprise" ? "live_agent" : "agent")
          : "user",
        sender_name: entry.sender_name || undefined,
        agentName: fromAgent ? (entry.sender_name || "Support Agent") : undefined,
        timestamp: entry.ts || Date.now(),
      });
    });

    // Restore call-context cards at the top of the repaint. setMessages below
    // is a REPLACE, so the cards onJoined just restored would be painted over.
    // Built from the live block list so ids and edited text come through.
    const contextCards = callContextCards(callContextBlocksRef.current);
    if (contextCards.length) replayed.unshift(...contextCards);

    if (replayed.length > 0) {
      setMessages(replayed);
      hasTurnsRef.current = true;
    }
  }, [onBugs, onCases, endHydrate, inFlightRejoin, sessionLearning]);

  // ── Phase 4: handle tse:cleared with a new session_id ──────────────────
  const onCleared = useCallback((payload) => {
    const newId = payload?.session_id;
    const prevId = payload?.previous_session_id;
    // A cleared session hides the diagnosis popover and drops its state (15.4).
    resetBrain();
    resetNarration();
    inFlightRejoin.reset();
    commandBusy.release();
    sessionLearning.abandon();
    // When the server mints a new id on clear, update the ref and URL so
    // subsequent messages target the correct session.
    if (newId && prevId && newId !== prevId) {
      // The retired session ("closed" server-side) would otherwise keep
      // rendering as live in Recents until a fetch happens to land after the
      // write-behind outbox flushes it to Postgres — see the comment on
      // RECENTS_CLOSED_EVENT.
      if (hasRecentsRail) {
        window.dispatchEvent(new CustomEvent(RECENTS_CLOSED_EVENT, { detail: { sessionId: prevId } }));
      }
      sessionIdRef.current = newId;
      setSessionId(newId);
      // Synchronously, so the deferred send below and every later emit address
      // the successor rather than the id that was just retired.
      adoptSessionRef.current?.(newId);
      // The socket is no longer torn down and rebuilt when the id changes, so
      // the join that used to happen as a side effect of that rebuild has to be
      // asked for. It is not optional: tse:clear builds the successor session
      // without an owner, and the join is what stamps app_user_id onto it —
      // without which the first commit lands with user_id='' and the session
      // never appears in Recents.
      joinRef.current?.();
      // BEFORE the navigate, for the same reason onSessionCreated does it:
      // App.js keys TseDashboard5 on the route's session id, so a navigate onto
      // an id the route wrapper does not recognise as ours changes the key and
      // REMOUNTS this whole tree.
      //
      // That remount is what loses the carried-over message. The close-and-
      // continue path draws its bubble locally (handleResumeContextSwitch ->
      // appendMessage) and the server deliberately does not echo it back to the
      // sending socket, so the remount's empty `messages` is the only copy left.
      // The ANSWER still arrives — the socket is module-level and the new
      // instance joins the room — so the new session opens showing a reply with
      // nothing above it, and only a refresh (tse:replay, which reads the
      // message back off the server) puts the question there.
      //
      // It also silently undid handleClear's "the old transcript stays above
      // the divider": the divider and the scrollback it separates were both
      // thrown away a tick after being drawn.
      onSessionIdAssigned?.(newId);
      if (!embedded) {
        navigate(`/TseDashboard5/${encodeURIComponent(newId)}`, { replace: true });
      }
    }
    // The clear is done: sessionIdRef names the successor and its own join has
    // already gone out above, so the next tse:joined/tse:replay to arrive is
    // the successor's and must be allowed through. Outside the branch so a
    // server that clears in place (no new id) releases the gate too — leaving
    // it armed would disable replay until the safety timeout.
    endClearInFlight();
    // Fix 3: if a context-switch stashed a query to send after the new id
    // landed, fire it now — exactly once — and arm the turn state.
    // sendMessage (from useTseSocket) closes over the sessionId from the
    // previous render, so we emit directly on the socket with the freshly
    // updated sessionIdRef to guarantee the message targets the new session.
    const deferred = deferredSendRef.current;
    if (deferred && socketInstanceRef.current) {
      deferredSendRef.current = null;
      streamingMsgIdRef.current = null;
      setIsStreaming(true);
      turnStartRef.current = Date.now();
      turnPendingRef.current = true;
      // This is the first turn of the successor session, but it does not go
      // through handleSend — which is the only other place the placeholder
      // Recents row is dispatched from. Without this the new chat is missing
      // from the rail until its turn commits AND the outbox flushes it to
      // Postgres AND a fetch happens to land, which is many seconds of the
      // engineer looking at a session that is not in their own history.
      if (hasRecentsRail && sessionIdRef.current) {
        window.dispatchEvent(new CustomEvent(RECENTS_OPTIMISTIC_EVENT, {
          detail: { sessionId: sessionIdRef.current, title: deferred },
        }));
      }
      socketInstanceRef.current.emit("tse:message", {
        session_id: sessionIdRef.current,
        message: deferred,
      });
    }
  }, [embedded, hasRecentsRail, navigate, onSessionIdAssigned, endClearInFlight, resetBrain, resetNarration, inFlightRejoin, commandBusy, sessionLearning]);

  /**
   * Freeze the question the user just answered into the transcript, options and
   * all, with the chosen one marked. Read-only — the live card above the
   * composer stays the only place anything is clickable.
   */
  const appendAnsweredQuestion = useCallback((snapshot, answer) => {
    appendMessage({
      type: "answered_question",
      sender: "agent",
      text: answer,
      // pills: a Diagnostic Agent card (diagnosis_id) keeps its pill layout once answered
      questionSnapshot: { question: snapshot?.question, options: snapshot?.options || [], pills: Boolean(snapshot?.diagnosis_id) },
      selectedAnswer: answer,
      // Two distinct times on one entry: askedAt is when the copilot put the
      // question, timestamp (set by appendMessage) is when you answered it.
      askedAt: snapshot?.askedAt,
    });
  }, [appendMessage]);

  // ── Multi-tab: the engineer's own message, sent from ANOTHER tab ───────
  // The sending tab renders its bubble locally and the server skips its socket,
  // so this only ever runs in the OTHER tabs on this session. It mirrors the
  // local send/answer path — bubble in, question card cleared — and deliberately
  // touches no turn state: this tab did not start the turn, and clearing
  // streaming/pending flags here would cut the legs off one it did.
  const echoedIdsRef = useRef(new Set());
  const onUserMessage = useCallback((payload) => {
    // If escalated, customer messages go to LHS and shouldn't show on RHS AI Copilot
    if (escalationActiveRef.current) return;
    const echoId = String(payload?.id || "");
    const text = typeof payload?.text === "string" ? payload.text : "";
    if (!text) return;
    // The socket is shared across sessions the same way onJoined's ack is: an
    // echo for a session this tab has moved off must not land in this one.
    const echoedSession = String(payload?.session_id || "");
    if (echoedSession && sessionIdRef.current && echoedSession !== sessionIdRef.current) return;
    // A reconnect can redeliver; without this the bubble is drawn twice. The
    // set is bounded because a session is a few hundred turns at the very most,
    // and it dies with the tab.
    if (echoId) {
      if (echoedIdsRef.current.has(echoId)) return;
      echoedIdsRef.current.add(echoId);
    }
    lockSourceReplacement();
    // Same three-way split handleSend / handleAnswer use, read against THIS
    // tab's copy of the question, so both tabs freeze the identical card.
    const snapshot = currentQuestion;
    const isFollowups = String(snapshot?.question || "") === FOLLOWUPS_MARKER;
    const matched = snapshot?.options?.find(
      (opt) => opt.trim().toLowerCase() === text.trim().toLowerCase(),
    );
    if (isFollowups) {
      setMessages((prev) => prev.map((m) => (
        m.type === "followups" && !m.answered
          ? { ...m, answered: true, selected: matched || text }
          : m
      )));
      appendMessage({ sender: "agent", text });
    } else if (matched) {
      appendAnsweredQuestion(snapshot, matched);
    } else {
      appendMessage({ sender: "agent", text });
    }
    // Free text sent while the server still held a pending answer is ambiguous
    // here for exactly the reason it is in handleSend, so the snapshot is armed
    // for onIdle to maybe restore. A literal option match resolved it,
    // followups were consumed above, and answered_question false means there
    // was nothing pending server-side to restore in the first place (a server
    // predating the field omits it, which lands on the same safe branch).
    pendingQuestionSnapshotRef.current =
      (matched || isFollowups || !payload?.answered_question) ? null : snapshot;
    setCurrentQuestion(null);
  }, [appendMessage, appendAnsweredQuestion, currentQuestion, lockSourceReplacement]);

  // ── The session is born here ──────────────────────────────────────────
  // Reply to tse:start. Until this lands the chat has no id, no address and no
  // Recents row; from here it has all three. Everything the engineer did to
  // trigger it is waiting in pendingIntentsRef and runs on the way out, so the
  // mint is invisible: they typed a message, and the message goes.
  const onSessionCreated = useCallback((payload) => {
    const id = String(payload?.session_id || "");
    if (!id || sessionIdRef.current) return;   // already named; ignore a late ack

    if (startTimeoutRef.current) {
      window.clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
    startPendingRef.current = false;
    sessionIdRef.current = id;
    setSessionId(id);
    // The hook's own ref, moved synchronously — the emitters below have to
    // target this id in THIS tick, not after the re-render the navigate causes.
    adoptSessionRef.current?.(id);
    // Before the navigate, so the route wrapper already knows this id is one of
    // its own by the time it re-renders on it and does not remount the tree.
    onSessionIdAssigned?.(id);

    if (!embedded) {
      // ?customer= survives the rewrite: it is what binds the left pane to the
      // escalated customer, so dropping it here would leave a reload showing a
      // copilot session with no live chat attached to it.
      const customerQuery = customerSessionIdRef.current
        ? `?customer=${encodeURIComponent(customerSessionIdRef.current)}`
        : "";
      navigate(`/TseDashboard5/${encodeURIComponent(id)}${customerQuery}`, { replace: true });
      // The server writes the real Recents row only after the turn commits and
      // flushes, many seconds from now. This is the placeholder, and it could
      // not be dispatched from handleSend the way it used to be: there was no
      // id to put on it yet.
      const title = pendingTitleRef.current;
      pendingTitleRef.current = null;
      if (title) {
        window.dispatchEvent(new CustomEvent(RECENTS_OPTIMISTIC_EVENT, {
          detail: { sessionId: id, title },
        }));
      }
    }

    // Call context attached before the session existed was buffered locally —
    // the emit was a no-op with no id to address. Flush it now, ahead of the
    // pending intent, so a first message that depends on it is served the same
    // way it would be on an established session.
    if (callContextBlocksRef.current.length > 0) pushCallContextRef.current?.();

    const intents = pendingIntentsRef.current;
    pendingIntentsRef.current = [];
    intents.forEach((run) => run());
  }, [embedded, navigate, onSessionIdAssigned]);

  const onLiveAgentMessage = useCallback((msg) => {
    if (variant !== "enterprise") return;
    const text = typeof msg?.text === "string" ? msg.text.trim() : "";
    if (!text) return;
    updateEscalationActive(true);
    if (msg?.agentName) recordLiveAgentName(msg.agentName);
    appendMessage({
      sender: "live_agent",
      text,
      agentName: msg?.agentName || liveAgentName || "Support Agent",
      timestamp: msg?.timestamp || Date.now(),
    });
  }, [variant, appendMessage, liveAgentName, recordLiveAgentName]);

  const sanitizeCustomerUsername = useCallback((name) => {
    const trimmed = (name || "").trim();
    const lower = trimmed.toLowerCase();
    if (!trimmed || ["unknown", "user", "agent", "tse agent", "customer"].includes(lower)) {
      if (variant === "enterprise") {
        const fallback = (loginUsername || localStorage.getItem("username") || "").trim();
        return fallback || "Customer";
      }
      return "Customer";
    }
    return trimmed;
  }, [loginUsername, variant]);

  const onCustomerUsername = useCallback((data) => {
    if (data?.username) {
      setCustomerUsername(sanitizeCustomerUsername(data.username));
    }
    if (data?.ai_agent_name) {
      setCustomerAiAgentName(String(data.ai_agent_name).trim());
    }
  }, [sanitizeCustomerUsername]);

  // Ack for tse:bind_escalation. This is what turns the escalation UI on for
  // the ENGINEER — his own copilot session is never escalated, so the
  // escalation_active flag on its join ack is always false.
  // The engineer on the other end dropped. Shown on the CUSTOMER's surface
  // only: the engineer's own view of the customer dropping is the
  // CustomerChatPanel header, which is a different pane with a different
  // audience. `attending` is non-null only on the engineer's route, which is
  // what tells the two surfaces apart — the same test onLiveChatEnded makes.
  const onLiveChatPeer = useCallback((payload) => {
    if (payload?.party !== "agent") return;
    if (customerSessionIdRef.current) return;
    if (payload.state === "reconnecting") {
      const left = payload.seconds_left;
      // No auto-clear timer: this notice is true until the server says
      // otherwise, and a message that vanished on its own would leave the
      // customer typing to someone who is not there.
      setAgentJoinedNotice(
        left ? `Reconnecting to your agent… ${left}s` : "Reconnecting to your agent…",
      );
    } else if (payload.state === "reconnected") {
      setAgentJoinedNotice("Your agent is back.");
      setTimeout(() => setAgentJoinedNotice(""), 4000);
    }
    // "lost" says nothing here: tse:live_chat_ended follows immediately and
    // owns the wording, so handling it twice would race two banners.
  }, []);

  const onLiveChatEnded = useCallback((payload) => {
    const from = payload?.session_id || payload?.customer_session_id;
    const attending = customerSessionIdRef.current;
    if (attending && from && String(from) !== attending && variant !== "enterprise") {
      return;
    }
    // On the customer's surface an ended live chat always means "back to the
    // AI", whoever pressed End chat: the server has already cleared
    // escalation_active, so locking the composer here would leave the agent's
    // avatar up and the composer dead with no one coming.
    //
    // ...but ONLY on their own surface. Both ends run this component, and the
    // server sends this same event to the attending engineer (to=agent_sid).
    // Taking the back-to-AI branch there unlocked the ENGINEER: liveChatEnded
    // went false, `chatEnded` in CustomerChatPanel went false with it, and
    // since customerSessionIdRef is read once at mount and never cleared, its
    // `canType` stayed true — composer live, send button live, messages still
    // relayed. `attending` is non-null only on the engineer's route (it comes
    // from ?customer=, and /ent-user is embedded), so it is what tells the two
    // surfaces apart.
    if (!attending) {
      liveChatEndedRef.current = false;
      setLiveChatEnded(false);
      setLiveChatEndedBy(null);
      updateEscalationActive(false);
      setAgentJoinedNotice(payload?.message || "You're back with the AI assistant.");
      setTimeout(() => setAgentJoinedNotice(""), 8000);
      return;
    }
    liveChatEndedRef.current = true;
    setLiveChatEnded(true);
    setLiveChatEndedBy(payload?.endedBy === "customer" ? "customer" : "agent");
    if (variant !== "enterprise") {
      updateEscalationActive(false);
    }
    const notice = payload?.message || "Live chat ended — connection closed.";
    setAgentJoinedNotice(notice);
    setTimeout(() => setAgentJoinedNotice(""), 8000);
  }, [variant]);

  const onEscalationBound = useCallback((payload) => {
    updateEscalationActive(true);
    if (payload?.customer_username) {
      setCustomerUsername(sanitizeCustomerUsername(payload.customer_username));
    }
    if (payload?.ai_agent_name) {
      setCustomerAiAgentName(String(payload.ai_agent_name).trim());
    }
    if (typeof payload?.sentiment === "number") {
      setLocalSentiment((current) => {
        setLocalPrevSentiment(current);
        return payload.sentiment;
      });
    }
    setAgentJoinedNotice("Connected to the customer.");
    setTimeout(() => setAgentJoinedNotice(""), 6000);
  }, [sanitizeCustomerUsername]);

  const onTseEscalated = useCallback(() => {
    updateEscalationActive(true);
    clearCopilotTurnState();
    setAgentJoinedNotice("Your session has been escalated. A live agent will join shortly.");
    setTimeout(() => setAgentJoinedNotice(""), 8000);
    // Late implicit-search status rows can land a few seconds after escalation.
    window.setTimeout(() => purgeTransientMessages(), 500);
    window.setTimeout(() => purgeTransientMessages(), 3000);
    window.setTimeout(() => purgeTransientMessages(), 8000);
  }, [clearCopilotTurnState, purgeTransientMessages]);

  const onAgentJoined = useCallback((payload) => {
    updateEscalationActive(true);
    clearCopilotTurnState();
    if (payload?.agentName) recordLiveAgentName(payload.agentName);
    setAgentJoinedNotice("A live agent has joined the chat.");
    setTimeout(() => setAgentJoinedNotice(""), 5000);
  }, [clearCopilotTurnState, recordLiveAgentName]);

  // ── V5 diagnosis events (W7b / F2) ─────────────────────────────────────
  // tse:diag_state feeds the popover only; the question itself still arrives
  // on tse:question and stays in this chat (15.3). Enterprise: the popover is
  // hidden (O9, 16.1), so the state is not even kept.
  const onDiagState = useCallback((payload) => {
    if (variant === "enterprise") return;
    applyDiagState(payload);
  }, [applyDiagState, variant]);

  // Every ack clears the pending flag; a diagnosis_prompt the server ignored
  // (stale prompt_id) or rejected goes back to clickable only if still open.
  const onCommandResult = useCallback((payload) => {
    applyCommandResult(payload);
    commandBusy.settle(payload);
    sessionLearning.onResult(payload);
    if (payload?.command === TEACH_COMMAND) {
      const close = closeAfterTeachRef.current && payload.status === "applied";
      closeAfterTeachRef.current = false;
      if (close) closeSessionRef.current?.(TEACH_CLOSE_SUMMARY);
    }
    const promptId = payload?.prompt_id;
    if (!promptId) return;
    const answered = payload.status === "applied" || payload.reason === "stale_prompt_id";
    setMessages((prev) => prev.map((m) => (
      m.type === "diagnosis_prompt" && m.id === promptId
        ? { ...m, status: answered ? PROMPT_STATUS.DONE : PROMPT_STATUS.OPEN }
        : m
    )));
  }, [applyCommandResult, commandBusy, sessionLearning]);

  // At most one open prompt per diagnosis: a newer one replaces the older
  // (contract). A replay of the same id updates it in place.
  const onDiagnosisPrompt = useCallback((payload) => {
    if (!payload?.id || payload?.type !== "diagnosis_prompt") return;
    setMessages((prev) => {
      const exists = prev.some((m) => m.type === "diagnosis_prompt" && m.id === payload.id);
      const next = prev.map((m) => {
        if (m.type !== "diagnosis_prompt") return m;
        if (m.id === payload.id) return { ...m, ...payload, status: m.status || PROMPT_STATUS.OPEN };
        return m.status === PROMPT_STATUS.DONE ? m : { ...m, status: PROMPT_STATUS.SUPERSEDED };
      });
      return exists ? next : [...next, { ...payload, status: PROMPT_STATUS.OPEN, timestamp: Date.now() }];
    });
  }, []);

  // One learning row per diagnosis: a later variant (teach -> sent) replaces
  // the earlier row of the same diagnosis_id. Rendering (and the enterprise
  // suppression) is CopilotPanel's.
  const onLearningPrompt = useCallback((payload) => {
    if (!payload?.id || payload?.type !== "learning_prompt") return;
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.type === "learning_prompt"
        && (m.id === payload.id || (payload.diagnosis_id && m.diagnosis_id === payload.diagnosis_id)));
      if (idx === -1) return [...prev, { ...payload, timestamp: Date.now() }];
      const next = prev.slice();
      next[idx] = { ...prev[idx], ...payload };
      return next;
    });
  }, []);

  // Every handler here must also be in the dependency list below (a handler
  // defined but not listed was once silently dropped).
  const handlers = useMemo(() => ({
    onChunk, onSourcesPreview, onSourcesEnriched, onSourceReplacementStarted, onSourceReplacementComplete, onSourceReplacementError, onQuestion, onInfo, onStatus, onBugs, onCases, onCaseAnalysis, onDone, onError, onAccessDenied, onNodeVisited, onIdle, onFeedbackPrompt, onSessionExpired, onMode, onProductLocked, onProductChangeAccepted, onProductChangeRefused, onDisconnected, onConnected, onJoined, shouldRejoin, onReplay, onCleared, onUserMessage, onSessionCreated, onSentimentUpdate,
    onLiveAgentMessage, onTseEscalated, onEscalationBound, onAgentJoined, onTseAgentJoined: onAgentJoined, onCustomerUsername, onLiveChatEnded, onLiveChatPeer,
    onDiagState, onCommandResult, onDiagnosisPrompt, onLearningPrompt,
    onDaNarrationPending, onDaNarrationStart, onDaNarrationDelta, onDaNarrationEnd, onDaNarrationAbort,
  }), [onDiagState, onCommandResult, onDiagnosisPrompt, onLearningPrompt, onDaNarrationPending, onDaNarrationStart, onDaNarrationDelta, onDaNarrationEnd, onDaNarrationAbort, onChunk, onSourcesPreview, onSourcesEnriched, onSourceReplacementStarted, onSourceReplacementComplete, onSourceReplacementError, onQuestion, onInfo, onStatus, onBugs, onCases, onCaseAnalysis, onDone, onError, onAccessDenied, onNodeVisited, onIdle, onFeedbackPrompt, onSessionExpired, onMode, onProductLocked, onProductChangeAccepted, onProductChangeRefused, onDisconnected, onConnected, onJoined, shouldRejoin, onReplay, onCleared, onUserMessage, onSessionCreated, onSentimentUpdate, onLiveAgentMessage, onTseEscalated, onEscalationBound, onAgentJoined, onCustomerUsername, onLiveChatEnded, onLiveChatPeer, clearCopilotTurnState, recordLiveAgentName, sanitizeCustomerUsername, loginUsername, variant]);

  // The last argument tells the server WHICH human is on this session. It is a
  // fallback the server only consults when the role behind the socket's token
  // cannot be read; sentiment scoring, auto-escalation and escalation keyword
  // detection are all customer-only, and firing them on a copilot locks it.
  const { sendMessage, sendAction, sendCommand, sendAnswer, replaceSource, citeSource, stop, sendImplicitSearch, clearSession, exitGuided, changeProduct, closeSession, join, reauthenticate, setCallContext: attachCallContext, startSession, adoptSession, bindEscalation, endLiveChat, syncAiAgentName, socket, isConnected } = useTseSocket(sessionIdRef.current, handlers, loginUsername, agentNameOverride, variant === "enterprise" ? "enterprise" : "copilot");

  useEffect(() => {
    if (!agentNameOverride || agentNameOverride === "AI Agent") return;
    // Re-run when the session id arrives. The name is often known first
    // (ChatWindow's room participants), and a sync that fires before the TSE
    // session exists is dropped — which left the transcript on a different
    // random persona than the one in the customer's header.
    syncAiAgentName(agentNameOverride);
  }, [agentNameOverride, syncAiAgentName, sessionId, isConnected]);
  const handleEndLiveChat = useCallback(() => {
    // customerSessionIdRef is the id of the customer this ENGINEER is
    // attending, and it is null when embedded (it is read from ?customer=,
    // which only the engineer's route carries). On /ent-user the customer IS
    // the session, so their own id is the one to end — without this fallback
    // the button would return here and do nothing at all.
    const customerId = customerSessionIdRef.current || sessionIdRef.current;
    if (!customerId || liveChatEndedRef.current) return;
    endLiveChat(customerId);
  }, [endLiveChat]);
  implicitSearchRef.current = sendImplicitSearch;
  // onDone is defined ABOVE this line, so it cannot close over `closeSession`
  // directly — the binding does not exist yet when that callback is built.
  // Without this assignment closeSessionRef stays null and the hybrid close
  // checkpoint fires into nothing.
  closeSessionRef.current = closeSession;
  socketInstanceRef.current = socket;
  // Assigned on every render because onSessionCreated is defined ABOVE the hook
  // that produces them — it has to be, to go into `handlers`.
  adoptSessionRef.current = adoptSession;
  joinRef.current = join;
  // Same story: onAccessDenied is built above the hook that produces this.
  reauthenticateRef.current = reauthenticate;

  const handleReplaceSource = useCallback((message, source, number, candidate) => {
    if (
      isStreaming
      || !message?.canReplaceSource
      || !message?.sourceTurnId
      || !candidate?.candidate_id
    ) return;
    patchMessage(message.id, {
      canReplaceSource: false,
      replacingSourceNumber: number,
      sourceActionKind: "replace",
    });
    replaceSource(
      message.sourceTurnId,
      source?.id || `S${number}`,
      candidate.candidate_id,
    );
  }, [isStreaming, patchMessage, replaceSource]);

  const handleCiteSource = useCallback((message, source, number) => {
    if (isStreaming || !message?.canReplaceSource || !message?.sourceTurnId) return;
    patchMessage(message.id, {
      canReplaceSource: false,
      replacingSourceNumber: number,
      sourceActionKind: "cite",
    });
    citeSource(message.sourceTurnId, source?.id || `S${number}`);
  }, [isStreaming, patchMessage, citeSource]);

  // ── Anything that needs a session, on a chat that has none ────────────
  // Runs `intent` at once when the session already exists, which is every case
  // but the first. Otherwise it queues the intent, asks the server for an id,
  // and replays the queue when it lands. Only ONE tse:start goes out however
  // many intents pile up behind it — a second would mint a second session and
  // orphan the first.
  const withSession = useCallback((intent) => {
    if (sessionIdRef.current) {
      intent();
      return;
    }
    pendingIntentsRef.current.push(intent);
    if (startPendingRef.current) return;   // already asked; the ack replays it
    startPendingRef.current = true;
    startSession();
    // A mint that never comes back would swallow the message silently — the
    // bubble is already in the transcript and the composer is armed for an
    // answer that cannot arrive. Say so instead.
    startTimeoutRef.current = window.setTimeout(() => {
      startTimeoutRef.current = null;
      startPendingRef.current = false;
      if (sessionIdRef.current) return;
      pendingIntentsRef.current = [];
      setIsStreaming(false);
      turnPendingRef.current = false;
      appendMessage({
        type: "error",
        text: "Could not start a session. Check your connection and try again.",
      });
    }, START_SESSION_TIMEOUT_MS);
  }, [startSession, appendMessage]);

  // ── Claiming the live-agent seat on the escalated customer ────────────
  // As soon as the transport is up, not on the engineer's first prompt: the
  // left pane can neither send nor receive until the server knows which socket
  // holds the seat, and the first thing the engineer does is read what the
  // customer has already said.
  //
  // Through withSession because this tab opens on /new — the bind names BOTH
  // ids, so the copilot session has to exist first. It is minted exactly the
  // way a first message would mint it.
  useEffect(() => {
    const customerId = customerSessionIdRef.current;
    if (!isConnected || !customerId) return;
    const sid = socket?.id || null;
    if (!sid || boundSocketIdRef.current === sid) return;
    boundSocketIdRef.current = sid;
    withSession(() => bindEscalation(customerId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, socket, withSession]);

  // Offered when the join timed out. Re-issues tse:join on the live socket —
  // the transport is usually back by then, and `connect` will not fire a second
  // time to re-join on its own.
  const handleRetryHydrate = useCallback(() => {
    setHydrateFailed(false);
    setHydrating(true);
    join();
  }, [join]);

  /**
   * "Resume session" on the terminal banner.
   *
   * The engineer's only way forward used to be abandoning the session. But a
   * lock is not proof the session is gone: `sessionEndReason === "disconnect"`
   * means only the TRANSPORT died, and the server holds the session for 24h and
   * replays the whole transcript on join — so a rejoin genuinely brings it back.
   * A real idle timeout cannot be resumed, but the attempt is still safe (see
   * the lock release below), so the button is offered for both and the banner
   * wording stops promising success for the timeout case.
   */
  const resumeInFlightRef = useRef(false);
  // The ref is what guards re-entry (it must be readable synchronously); this is
  // its render-visible twin. Without it the banner unmounts the moment Resume
  // clears sessionExpired, so the engineer clicks and everything simply
  // disappears — the one moment they most need to be told something is happening.
  const [resumePending, setResumePending] = useState(false);
  const handleResumeSession = useCallback(() => {
    // Idempotent under a double-click: a second attempt would reconnect the
    // socket out from under the first one's in-flight join.
    if (resumeInFlightRef.current) return;
    resumeInFlightRef.current = true;
    setResumePending(true);
    // Reset BEFORE arming: armReconnectTimer caps every arm at
    // `RECONNECT_GRACE_MS - (now - disconnectedSince)`, and disconnectedSince
    // still holds the stamp of the outage that just locked us — which is by
    // definition older than the whole grace window. Left alone, `remaining <= 0`
    // and the timer fires lockAsDisconnected() synchronously, re-locking before
    // the reconnect has had a single tick. This attempt gets its own window.
    disconnectedSinceRef.current = null;
    // THE dangerous line: this re-opens the socket's auto-rejoin, and rejoining
    // an id the server no longer holds mints a BLANK session behind the
    // transcript still on screen. It is safe only because the server adjudicates
    // — a join for a genuinely expired id is REFUSED and answered with
    // tse:session_expired (onSessionExpired re-locks), and a join that lands on a
    // fresh session reports `resumed: false`, which onJoined turns straight back
    // into lockAsDisconnected(). Neither path leaves an empty session live behind
    // the old conversation. Nothing else here may release this lock.
    sessionLockedRef.current = false;
    // Optimistically leave the terminal state for the RECOVERABLE one: the
    // transcript stays usable, the composer stays held, and the banner is
    // replaced by the reconnecting affordances the disconnect path already has.
    setSessionExpired(false);
    endSession(null);
    setReconnecting(true);
    const sock = socketInstanceRef.current;
    if (sock?.connected) {
      // Transport is fine — the lock is what kept us unjoined, and `connect`
      // will not fire again to correct that. Ask for the join explicitly.
      join();
    } else {
      // Refresh the token as well as the socket. A session that sat idle long
      // enough to time out has very likely outlived its access token too, so a
      // bare reconnect would come back anonymous and be denied. reauthenticate()
      // reconnects, and the connect handler re-joins now that the lock is off.
      reauthenticateRef.current?.();
    }
    // A resume that never lands must not spin: the grace timer takes it back to
    // lockAsDisconnected() and the banner returns.
    armReconnectTimer(RECONNECT_GRACE_MS);
  }, [join, armReconnectTimer, endSession]);

  // ── Press Resume for the customer ─────────────────────────────────────────
  // ENTERPRISE ONLY. The engineer keeps the manual button: they are debugging a
  // session and may want to see that it dropped.
  //
  // A customer should never be shown "Connection lost — try resuming it" for an
  // outage that is already over. It happens because the lock is one-way:
  // `shouldRejoin` (useTseSocket:198) returns false once locked, so `connect`
  // stops short of emitJoin and NO tse:joined can arrive — which is why clearing
  // the lock inside onJoined was not enough on its own, the ack it waits for is
  // never sent. The hook's own comment says the same thing: "unlocking on its
  // own cannot recover the session. The caller needs a way to ask for the join
  // explicitly."
  //
  // So ask. This is exactly the button's code path, including its adjudication:
  // a genuinely dead id is refused with tse:session_expired and a fresh one
  // answers `resumed: false`, both of which re-lock. The customer is not being
  // spared a real failure, only a recovered one.
  //
  // Fires at most once per lock — the ref clears only when the lock does, so a
  // resume that fails leaves the banner up and does not spin.
  const autoResumedRef = useRef(false);
  useEffect(() => {
    if (variant !== "enterprise") return;
    const lockedByDisconnect = sessionExpired && sessionEndReason === "disconnect";
    if (!lockedByDisconnect) {
      autoResumedRef.current = false;
      return;
    }
    // Wait for the transport. handleResumeSession can reconnect on its own, but
    // firing while the socket is genuinely down spends the one attempt on the
    // outage instead of on the recovery.
    if (!isConnected || autoResumedRef.current) return;
    autoResumedRef.current = true;
    handleResumeSession();
  }, [variant, sessionExpired, sessionEndReason, isConnected, handleResumeSession]);

  // Every exit from a resume attempt — a successful join, an expiry refusal, or
  // the grace timer giving up — clears `reconnecting`, so this is the one place
  // that has to release the in-flight guard.
  useEffect(() => {
    if (!reconnecting) {
      resumeInFlightRef.current = false;
      setResumePending(false);
    }
  }, [reconnecting]);

  // `trigger` is the typed "/clear" text, stored server-side as the retired
  // session's closing message. Also bound to the header button as an onClick,
  // where the first argument is a click event — hence the string check.
  const handleClear = useCallback((trigger) => {
    const clearTrigger = typeof trigger === "string" ? trigger : "";
    // Unlocked FIRST: while the lock is set the socket's connect handler
    // refuses to rejoin, so clearing without releasing it would leave a
    // disconnected session that can never come back.
    sessionLockedRef.current = false;
    // Armed BEFORE anything that can provoke a replay — specifically the join()
    // below, which re-joins the session being retired and makes the server send
    // its tse:joined + tse:replay. From here until onCleared has swapped in the
    // successor id, no ack and no replay may seed this tab. See
    // clearInFlightRef for the two interleavings this covers.
    beginClearInFlight();
    // Nor a re-hydrate, a busy command or a learning request of the old session.
    inFlightRejoin.reset();
    commandBusy.release();
    sessionLearning.abandon();
    // A fresh session cannot inherit a pending reconnect from the old one.
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    setReconnecting(false);
    hasTurnsRef.current = false;
    endSession(null);
    // Re-join explicitly. While the lock was set the socket's connect handler
    // refused to join — and if the socket had already reconnected by then it is
    // connected but unjoined, with no further `connect` event coming to fix it.
    // Unlocking alone would leave "Start new session" pointing at a dead session
    // that only a page reload could recover.
    join();
    // Server swaps in a fresh TSESession under the same id, so the next message
    // starts a new diagnostic instead of continuing the old one.
    clearSession(clearTrigger);
    firstPromptRef.current = null;
    streamingMsgIdRef.current = null;
    copilotTurnRef.current = 0;
    turnPendingRef.current = false;
    turnStartRef.current = 0;
    costLedgerRef.current = createCostLedger();
    // Cleared with the rest: a breakdown left over from the old session would
    // otherwise be banked onto turn 1 of the new one.
    latestBreakdownRef.current = null;
    pendingQuestionSnapshotRef.current = null;
    lastFreeTextRef.current = "";
    contextSwitchQueryRef.current = null;
    setCurrentQuestion(null);
    setIsStreaming(false);
    setCopilotMode("fast");
    // The server drops this session's conversation memory too
    // (tse_clear -> conversation_context.clear), and the id is REUSED, so
    // nothing else tells the context panel its contents are gone.
    setCtxResetKey((k) => k + 1);
    // Starting over is not a walkthrough to rebind to: without this a refresh
    // after "New session" would still resume, on a guided flag left by the
    // session that was just cleared.
    rememberSessionMode("fast");
    // tse:clear is the one thing a timed-out id still accepts — the server
    // retires the expiry marker and builds a fresh session behind it.
    setSessionExpired(false);
    // A close is only terminal for the session it closed — the fresh one behind
    // this clear takes messages again.
    setSessionClosed(false);
    setSessionStats({ turns: 0, totalCostUsd: 0, totalLatencyMs: 0, turnLog: [] });
    sidebar.setBugs([]);
    sidebar.setCases([]);
    setResultsMeta({ bugs: {}, cases: {} });
    // A CLEAN transcript, not the old one above a divider.
    //
    // Keeping the scrollback was right when a clear reused the same session id.
    // It does not any more: tse:clear mints a NEW id and onCleared navigates to
    // it, so the retained transcript ends up rendered under a URL that names a
    // different session — and reloading that URL replays only the new session's
    // turns. Runtime and reload disagreed about what the page contains, which is
    // how a brand-new chat came to show the closed diagnostic it was started
    // from (observed 2026-08-25: the parent's whole conversation, its wrap-up
    // and its "session is closed" banner, all sitting above the first answer of
    // the child). The old conversation is not lost — it is a session of its own
    // now, one click away in Recents.
    setMessages([]);
    resetNarration();
    setProductLocked(false);
    productChangeRef.current = null;
    setChangePending(null);
  }, [beginClearInFlight, clearSession, join, sidebar, endSession, setChangePending, resetNarration, inFlightRejoin, commandBusy, sessionLearning]);

  const handleSend = useCallback((text, attachments = [], stagedAnswers = []) => {
    const trimmed = (text || "").trim();
    // A prompt is required either way -- attachments ride along with it (see
    // CopilotPanel.handleSend) but there's always real text to send.
    if (!trimmed) return;

    const lower = trimmed.toLowerCase();
    if (lower === "/clear" || lower === "clear") {
      handleClear(trimmed);
      return;
    }

    // Anything else under a timed-out id would be refused server-side; /clear
    // above is the sanctioned way out.
    if (sessionExpired) return;
    // Belt and braces: the composer is disabled after a close, so this is only
    // reachable from a programmatic send.
    if (sessionClosed) return;

    // Live chat with a human — not a copilot turn. The server relays this to
    // live_agent_sid and answers with an empty tse:done; arming isStreaming
    // here is what made every customer message look like "davis is thinking…"
    // and left "Searching search_cases…" rows from a turn that never ran.
    if (escalationActiveRef.current && variant === "enterprise") {
      if (liveChatEndedRef.current) return;
      appendMessage({ sender: "user", text: trimmed, attachments });
      if (attachments.length) {
        withSession(() => {
          uploadAttachments(sessionIdRef.current, attachments)
            .then(({ ids, failed }) => {
              if (failed.length) {
                appendMessage({ type: "error", text: `Could not attach: ${failed.join("; ")}` });
              }
              sendMessage(trimmed, ids);
            })
            .catch(() => sendMessage(trimmed));
        });
      } else {
        withSession(() => sendMessage(trimmed));
      }
      return;
    }

    // Repeat clicks before the next render still read isStreaming as false.
    if (!sendGuard.claim()) return;
    inFlightRejoin.cancel();
    // Batch card picks staged before typing ride with the text (F5-02); without picks the send is unchanged.
    const sendTyped = (ids) => {
      if (stagedAnswers.length) sendMessage(trimmed, ids || [], stagedAnswers);
      else if (ids) sendMessage(trimmed, ids);
      else sendMessage(trimmed);
    };

    // The moment a later turn starts, every earlier source row becomes
    // immutable. The server changes its turn token at the same boundary, so
    // this immediate UI lock is backed by authoritative stale-request rejection.
    lockSourceReplacement();

    // The first message of a session is what makes it a session worth listing.
    // The row it gets in Recents is written server-side only after the turn is
    // committed and flushed, many seconds from now, so the sidebar is told to
    // draw a local row immediately and reconcile it against the real one when
    // the next fetch returns (SideBar: RECENTS_OPTIMISTIC_EVENT). Read BEFORE
    // the two lines below, which are what make it no longer the first turn.
    const isFirstTurn = !hasTurnsRef.current && !firstPromptRef.current;

    // From here on the server is holding context for us, so losing the socket
    // is worth telling the user about.
    hasTurnsRef.current = true;

    if (!firstPromptRef.current) firstPromptRef.current = trimmed;

    // Only mounts with a rail behind them have a row to draw — which now
    // includes enterprise, embedded though it is (see hasRecentsRail).
    if (isFirstTurn && hasRecentsRail) {
      if (sessionIdRef.current) {
        window.dispatchEvent(new CustomEvent(RECENTS_OPTIMISTIC_EVENT, {
          detail: { sessionId: sessionIdRef.current, title: trimmed },
        }));
      } else {
        // No id to put on the row yet. onSessionCreated draws it the moment
        // the server names the session — which is the same moment the address
        // bar stops saying /new, so the sidebar and the URL agree.
        pendingTitleRef.current = trimmed;
      }
    }

    // Typing an option verbatim is answering the question, so it gets the same
    // frozen card as clicking it. Freeform text stays a plain bubble.
    const snapshot = currentQuestion;
    const isFollowups = String(snapshot?.question || "") === FOLLOWUPS_MARKER;
    // A batch card's top-level options are only its first question's: typed text there is free text (card kept).
    const matched = !isBatchQuestion(snapshot) && snapshot?.options?.find((opt) => opt.trim().toLowerCase() === lower);
    // Optimistic edge highlight for typed answers that resolve to an MCQ option.
    // Same guided-only gate as handleAnswer.
    if (isFollowups) {
      // Typing consumes the suggestion bubbles the same way clicking does, so
      // a stale clickable list can't sit above a turn that already moved on.
      setMessages((prev) => prev.map((m) => (
        m.type === "followups" && !m.answered
          ? { ...m, answered: true, selected: matched || trimmed }
          : m
      )));
      appendMessage({ sender: "agent", text: trimmed, attachments });
    } else if (matched) {
      appendAnsweredQuestion(snapshot, matched);
    } else {
      appendMessage({ sender: "agent", text: trimmed, attachments });
    }
    if (!matched && !isFollowups) lastFreeTextRef.current = trimmed;

    // Overwritten fresh on every send, so this can never carry a stale value
    // from an earlier turn. A literal option match is a definite resolution —
    // nothing to restore. Free text is ambiguous (could be a real answer or a
    // fast_answer-routed aside) until the response comes back, so onIdle gets
    // the chance to put the card back if nothing actually advanced.
    // Never for followups: their bubbles were just consumed above, and onIdle
    // restoring the snapshot would put a suggestion menu back into
    // currentQuestion — reviving the composer placeholder for a menu that is
    // no longer on screen. Only diagnostic questions are worth restoring.
    pendingQuestionSnapshotRef.current = (matched || isFollowups) ? null : snapshot;

    setCurrentQuestion(null);
    // A user-initiated turn NEVER continues the previous bubble. onDisconnected
    // deliberately preserves this ref so chunks arriving after a rejoin rejoin their
    // own answer — but if the turn finished during the gap, its tse:done/tse:idle was
    // lost (emits are `to=sid`, and the server only re-sends idle for a session it had
    // to recreate). The ref would then still point at the old bubble and this turn's
    // answer would be appended to it. Clearing here is what bounds that.
    streamingMsgIdRef.current = null;
    setIsStreaming(true);
    setCtxTurnKey((k) => k + 1);
    turnStartRef.current = Date.now();
    turnPendingRef.current = true;
    // Attachments are uploaded here, not in the bubble: the transcript shows
    // what the engineer typed plus a chip, while the server turns the file into
    // text under the message (PDFs extracted, screenshots transcribed). The
    // upload is async and the send waits for it — a turn that raced ahead would
    // reach the copilot without the very file it is being asked about.
    if (attachments.length) {
      // INSIDE withSession, not before it. The id is minted by the server now,
      // so on the first message of a blank chat sessionIdRef.current is still
      // null -- uploading here would POST session_id=null, the server would
      // file the bytes under a session that never existed, and every id would
      // come back unresolvable. withSession runs this once the id is real.
      withSession(() => {
        uploadAttachments(sessionIdRef.current, attachments)
          .then(({ ids, failed }) => {
            // An upload that failed is said out loud rather than swallowed: the
            // answer would otherwise ignore the evidence with no explanation.
            if (failed.length) {
              appendMessage({ type: "error", text: `Could not attach: ${failed.join("; ")}` });
            }
            sendTyped(ids);
          })
          .catch(() => sendTyped());   // never strand the turn
      });
    } else {
      withSession(() => sendTyped());
    }
  }, [appendMessage, appendAnsweredQuestion, sendMessage, currentQuestion, handleClear, sessionExpired, sessionClosed, embedded, hasRecentsRail, withSession, variant, lockSourceReplacement, sendGuard, inFlightRejoin]);

  // The post-close banner's button. Always clears into a fresh session; when
  // the close came out of a context switch it also resends the message that
  // triggered it, so the engineer doesn't have to retype it.
  const handleResumeContextSwitch = useCallback((query) => {
    const trimmed = (query || "").trim();
    // No deferred utterance (the close came from clicking "Yes, close session"):
    // same button, but there is nothing to resend — just open the new session
    // and leave the composer to the engineer.
    if (!trimmed) {
      handleClear();
      return;
    }
    // Fix 3: stash the query and let onCleared send it once the new session
    // id has actually arrived. Sending synchronously here used sendMessage
    // which closes over the OLD session id — the server would file the turn
    // under the retired session and the fresh one would get zero turns.
    deferredSendRef.current = trimmed;
    // Clears the transcript itself now, so the carried-over message below is the
    // only thing in the successor session.
    handleClear();
    firstPromptRef.current = trimmed;
    appendMessage({ sender: "agent", text: trimmed });
  }, [handleClear, appendMessage]);

  // ── The ghost hand-off ────────────────────────────────────────────────
  //
  // The enterprise surface opens on a greeting screen. That screen is a GHOST:
  // this dashboard is already mounted underneath it, connected, with its own
  // session. The greeting only collects the first message and calls
  // `handleSend` above — the very function this dashboard's own composer
  // calls, published into a ref the wrapper owns. Then the
  // ghost disappears. Nothing remounts, nothing navigates, and the enterprise
  // chat runs the identical code path the copilot runs.
  //
  // WHAT THIS REPLACED. An `initialQuery` prop, auto-sent by an effect that
  // depended on `isConnected`. `isConnected` flips false->true on every
  // reconnect — an idle timeout, a proxy recycling the socket, a laptop waking
  // — and the prop never changed, so the effect re-ran and re-sent the same
  // text. One session came back with EIGHTEEN identical turns, 05:03 to 05:21,
  // one a minute: one per reconnect. The copilot never showed it because the
  // copilot never passed the prop.
  useEffect(() => {
    if (!sendRef) return undefined;
    sendRef.current = handleSend;
    return () => {
      if (sendRef.current === handleSend) sendRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendRef, handleSend]);

  const handleCaseAnalysisSubmit = useCallback(async (rawQuery) => {
    if (sessionExpired) return;
    const q = (rawQuery || "").trim();
    if (!q) return;
    lockSourceReplacement();
    // Read BEFORE hasTurnsRef is armed below, same ordering as handleSend.
    const isFirstTurn = !hasTurnsRef.current && !firstPromptRef.current;
    // Exempt from the "ask a question first" gate the other actions go through,
    // so this can be the turn that starts a session — arm the warning here too.
    hasTurnsRef.current = true;

    // Show the bubble immediately — the case-number lookup below is a network
    // round trip that used to block this, so pressing Enter looked like a
    // multi-second do-nothing. The backend already falls back to raw text via
    // get_case_by_id_or_number, so there's nothing this UI needs to wait on
    // before showing what was typed and starting the turn.
    const msgId = `case-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    appendMessage({ id: msgId, sender: "agent", text: `Analyze case ${q}.` });
    // Draw the placeholder Recents row now, exactly as handleSend does. Without
    // this, a session started FROM Case Analysis is missing from the rail until
    // its turn commits AND the outbox flushes it to Postgres AND a fetch happens
    // to land after that — the RECENTS_REFRESH_EVENT fired on turn-idle usually
    // loses the race with the flusher, so in practice the row only turned up on
    // the next page load. Titled from what was typed; the server's real row
    // replaces it on reconcile.
    if (isFirstTurn && hasRecentsRail) {
      const optimisticTitle = `Analyze case ${q}.`;
      if (sessionIdRef.current) {
        window.dispatchEvent(new CustomEvent(RECENTS_OPTIMISTIC_EVENT, {
          detail: { sessionId: sessionIdRef.current, title: optimisticTitle },
        }));
      } else {
        // Blank chat — no id yet. onSessionCreated draws the row the moment the
        // server names the session, which is when the URL stops saying /new.
        pendingTitleRef.current = optimisticTitle;
      }
    }
    pendingQuestionSnapshotRef.current = null;
    setCurrentQuestion(null);
    // A user-initiated turn NEVER continues the previous bubble. onDisconnected
    // deliberately preserves this ref so chunks arriving after a rejoin rejoin their
    // own answer — but if the turn finished during the gap, its tse:done/tse:idle was
    // lost (emits are `to=sid`, and the server only re-sends idle for a session it had
    // to recreate). The ref would then still point at the old bubble and this turn's
    // answer would be appended to it. Clearing here is what bounds that.
    streamingMsgIdRef.current = null;
    setIsStreaming(true);
    turnStartRef.current = Date.now();
    turnPendingRef.current = true;

    let caseRef = q;
    try {
      const res = await authFetch(`/api/open-cases/search?q=${encodeURIComponent(q)}`);
      if (res.ok) {
        const data = await res.json();
        const hit = data?.case || data;
        const id = hit?.caseId || hit?.case_id || hit?.id;
        const num = hit?.caseNumber || hit?.case_number;
        if (id || num) caseRef = String(num || id);
      }
    } catch (_) {
      // Fall through with raw query; backend resolves via get_case_by_id_or_number.
    }

    const text = `Analyze case ${caseRef}.`;
    // Only touches the bubble if the lookup actually resolved to something
    // different from what the engineer typed — no flicker on the common case.
    if (caseRef !== q) patchMessage(msgId, { text });
    // Same reason as the other send paths: never continue a bubble from a turn that
    // may have completed while the socket was down.
    streamingMsgIdRef.current = null;
    // Through withSession, exactly like handleSend. Case Analysis is allowed to be
    // the FIRST turn of a blank chat (the gate above is deliberately skipped), and
    // on a blank chat sessionIdRef.current is still null -- the id is minted by the
    // server. A bare sendMessage() returns without emitting in that state, so the
    // bubble and the "thinking" spinner would sit there forever with nothing ever
    // reaching the backend.
    withSession(() => sendMessage(text));
  }, [sessionExpired, appendMessage, patchMessage, sendMessage, withSession, hasRecentsRail, lockSourceReplacement]);

  const handleAnswer = useCallback((rawAnswer) => {
    if (sessionExpired) return;
    if (escalationActiveRef.current && variant === "enterprise") return;
    // A DA batch card hands over {kind: "batch", picks}: every question of the
    // card answered at once (utils/batchAnswer.js). Anything else is a caption.
    const batch = buildBatchAnswer(currentQuestion, rawAnswer);
    if (isBatchAnswer(rawAnswer) && !batch) return;
    const answer = batch ? batch.message : rawAnswer;
    // One answer per card, however many clicks land before the next render.
    if (!sendGuard.claim()) return;
    inFlightRejoin.cancel();
    lockSourceReplacement();
    // Optimistic edge highlight: mark the outgoing edge whose condition
    // matches the selected answer, so the map feels responsive while the
    // server processes the turn. Guided-only — fast mode does not advance
    // the map, so highlighting would be misleading.
    // A whole session can be driven by clicking MCQ options and suggestions
    // without ever typing, so this path has to arm the warning too.
    hasTurnsRef.current = true;
    if (!firstPromptRef.current) firstPromptRef.current = answer;
    // Followups already live in the transcript — mark the picked one and move
    // on. Freezing an answered MCQ card here would show the same suggestions
    // twice, once as bubbles and once as a card.
    if (batch) {
      // One compact bubble on your side, one line per question (question, answer).
      appendMessage({ type: "answered_batch", sender: "user", text: batch.message, lines: batch.lines, askedAt: currentQuestion?.askedAt });
    } else if (String(currentQuestion?.question || "") === FOLLOWUPS_MARKER) {
      setMessages((prev) => prev.map((m) => (
        m.type === "followups" && !m.answered
          ? { ...m, answered: true, selected: answer }
          : m
      )));
      appendMessage({ sender: "agent", text: answer });
    } else if (currentQuestion?.options?.length) {
      appendAnsweredQuestion(currentQuestion, answer);
    } else {
      appendMessage({ sender: "agent", text: answer });
    }
    pendingQuestionSnapshotRef.current = null;
    setCurrentQuestion(null);
    // A user-initiated turn NEVER continues the previous bubble. onDisconnected
    // deliberately preserves this ref so chunks arriving after a rejoin rejoin their
    // own answer — but if the turn finished during the gap, its tse:done/tse:idle was
    // lost (emits are `to=sid`, and the server only re-sends idle for a session it had
    // to recreate). The ref would then still point at the old bubble and this turn's
    // answer would be appended to it. Clearing here is what bounds that.
    streamingMsgIdRef.current = null;
    setIsStreaming(true);
    turnStartRef.current = Date.now();
    turnPendingRef.current = true;
    // V5 (W2 protocol, additive): a question that carried question_id + option
    // objects is answered with kind "answer", answer_to and option_id(s); any
    // other card keeps the legacy caption-as-text path.
    if (batch && sendAnswer({ answerTo: batch.answer_to, answers: batch.answers, message: batch.message })) return;
    const v5Answer = batch ? null : buildAnswer(currentQuestion, answer);
    if (v5Answer && sendAnswer({ answerTo: v5Answer.answer_to, optionId: v5Answer.option_id, optionIds: v5Answer.option_ids, message: v5Answer.message })) return;
    sendMessage(answer);
  }, [appendMessage, appendAnsweredQuestion, sendMessage, sendAnswer, currentQuestion, sessionExpired, variant, lockSourceReplacement, sendGuard, inFlightRejoin]);

  // ── V5 diagnosis popover and banners (W7b) ─────────────────────────────
  // Panel commands: fire-and-wait; the next tse:diag_state is the only thing
  // that changes what the panel shows (15.3).
  const { markCommandPending } = brain;

  // Session learning switch (final plan N12): shows the server-confirmed value only (useSessionLearning). Never
  // while a turn streams: the command's room tse:idle would end that turn on screen.
  const learningEnabled = sessionLearning.enabled;
  const learningPending = !!brain.pendingCommands?.[LEARNING_TOGGLE];
  const handleToggleLearning = useCallback((enabled) => {
    if (sessionExpired || isStreamingRef.current || sessionLearning.isPending()) return;
    if (sendCommand(LEARNING_TOGGLE, { enabled: !!enabled })) sessionLearning.begin(enabled);
  }, [sendCommand, sessionExpired, sessionLearning]);
  // The popover's Learning tab reads the same confirmed value as the header switch.
  // The teach card also shows its own progress (a teach ends the diagnosis and learns it, which takes a moment) and why a
  // teach was refused: client-only, from the pending flag and the last command_result.
  const teachPending = !!brain.pendingCommands?.teach;
  const lastTeachResult = brain.lastCommandResult?.command === "teach" ? brain.lastCommandResult : null;
  const teachRefused = !teachPending && lastTeachResult && lastTeachResult.status !== "applied"
    ? (lastTeachResult.reason || "refused") : null;
  const popoverDiagState = useMemo(() => {
    const ds = brain.diagState;
    if (!ds) return ds;
    const learning = ds.learning && ds.learning.enabled !== learningEnabled
      ? { ...ds.learning, enabled: learningEnabled } : ds.learning;
    return { ...ds, learning, teach_ui: { pending: teachPending, refused: teachRefused } };
  }, [brain.diagState, learningEnabled, teachPending, teachRefused]);

  const handleBrainCommand = useCallback((action, params = {}) => {
    if (action === LEARNING_TOGGLE) {
      handleToggleLearning(params.enabled);
      return;
    }
    if (sessionExpired) return;
    if (!sendCommand(action, params)) return;
    if (action === TEACH_COMMAND) closeAfterTeachRef.current = !!params.cause_key;   // free text stays a draft: the chat stays open
    markCommandPending(action);
    commandBusy.begin(action);
  }, [sendCommand, markCommandPending, sessionExpired, commandBusy, handleToggleLearning]);

  // Questions tab: the pending question is answered in this chat, so its row
  // focuses the composer through CopilotPanel (16.4).
  const focusComposer = useCallback(() => {
    copilotPanelRef.current?.focusComposer?.();
  }, []);

  const handleDiagnosisPrompt = useCallback((msg, confirm) => {
    const cmd = promptCommand(msg, confirm);
    if (!cmd || sessionExpired) return;
    if (!sendCommand(cmd.action, cmd.params)) return;
    markCommandPending(cmd.action);
    commandBusy.begin(cmd.action);
    patchMessage(msg.id, { status: PROMPT_STATUS.PENDING });
  }, [sendCommand, markCommandPending, patchMessage, sessionExpired, commandBusy]);

  // learned -> the Learning tab; teach -> the Diagnosis tab's teach card,
  // where the cause is named (the teach command goes out from the panel).
  const handleLearningPromptOpen = useCallback((msg) => {
    const teach = msg?.variant === "teach";
    setBrainOpenRequest({ tab: teach ? "diagnosis" : "learning", section: teach ? "teach" : undefined, nonce: Date.now() });
  }, []);

  // Shared by every action-bar button below (Case Analysis excepted — it never
  // routes through here, see handleCaseAnalysisSubmit) and by the feedback
  // button: none of them mean anything without a query to act on.
  const notifyNeedsQuery = useCallback((message) => {
    setFeedbackNotice(message);
    if (feedbackNoticeTimerRef.current) window.clearTimeout(feedbackNoticeTimerRef.current);
    feedbackNoticeTimerRef.current = window.setTimeout(() => setFeedbackNotice(""), 4000);
  }, []);

  const openFeedbackModal = useCallback(() => {
    if (!firstPromptRef.current) {
      notifyNeedsQuery("Ask a question first before sharing feedback.");
      return;
    }
    setShowFeedbackModal(true);
  }, [notifyNeedsQuery]);

  // Zoom binding — CustomerChatPanel owns the RTMS bridge connection (a
  // separate socket from the one useTseSocket talks to), so the bind call
  // goes through its ref, same as sendToCustomer/fillComposer.
  const handleZoomLinkConfirm = useCallback((meetingRef) => {
    // THROUGH withSession, because a bind NEEDS an id and on this branch a new
    // chat has none until the server mints one. Joining a call is usually the
    // first thing the engineer does — before any message — so the bind went out
    // with session_id="" and the server answered zoom:error and returned
    // BEFORE its own "Bound meeting_ref=..." log. Nothing was bound, every
    // transcript line was dropped with "NO BINDING", and the left pane sat on
    // "Waiting for call to connect" for the whole call while the RHS said it
    // was connecting.
    //
    // The id is read from the REF at execution time, not from the sessionId
    // prop: withSession replays the queue inside onSessionCreated, which sets
    // the ref synchronously and the state a render later. At replay the prop is
    // still the old value; the ref is already right.
    withSession(() => customerPanelRef.current?.bindZoomMeeting(meetingRef, sessionIdRef.current));
    setShowZoomModal(false);
    appendMessage({ type: "status", text: "Connecting Zoom call — live transcript will appear in the customer panel once it starts." });
  }, [withSession, appendMessage]);

  // "Join Call" — CustomerChatPanel's header button (left pane), not an
  // ActionBar action: the call happens on Zoom's own site (opened here in a
  // new tab); this only opens the "paste it back" modal so the resulting
  // transcript knows which session to reach. The ideal (backend creates the
  // meeting) swaps this one function's body out later without touching
  // anything downstream — modal, bind, and transcript pipeline all stay put.
  const handleJoinCall = useCallback(() => {
    // window.open("https://zoom.us/start", "_blank");
    setShowZoomModal(true);
  }, []);

  const handleAction = useCallback((action) => {
    // Every action needs at least one query asked this session first — same
    // rule as feedback above, but rendered under the specific ActionBar
    // button rather than the header slot notifyNeedsQuery uses, since that
    // slot sits next to a completely different button. Case Analysis is the
    // one exception, but it's structurally exempt already: it submits via
    // onCaseAnalysisSubmit, never through onAction/handleAction, so this
    // never runs for it.
    //
    // Attached call context satisfies the gate for the three searches that
    // run off the problem statement. The gate exists so a search can't fire
    // against an empty session; a call the Copilot has already been told
    // about is not an empty session, and making the engineer type a throwaway
    // question to unlock "find cases about this call" defeats the point. The
    // server side already reads that context — it goes into the conversation
    // the facts summary builds the query from
    // (TSESession._assemble_facts_conversation).
    const searchesCallContext = [
      "find_cases", "find_bugs", "active_incident", "possible_causes", "next_step",
    ].includes(action);
    const hasCallContext = callContextBlocksRef.current.length > 0;
    if (!firstPromptRef.current && !(searchesCallContext && hasCallContext)) {
      setActionNotice({ action, message: "Ask a question first" });
      if (actionNoticeTimerRef.current) window.clearTimeout(actionNoticeTimerRef.current);
      actionNoticeTimerRef.current = window.setTimeout(() => setActionNotice(null), 4000);
      return;
    }
    // Search opens the standalone RAG console, matching Dashboard2.
    if (action === "search") {
      window.open(`${window.location.origin}/TseAgentRag`, "_blank", RAG_SEARCH_WINDOW);
      return;
    }
    // Feedback is the shared Dashboard2 modal, not an agent turn.
    if (action === "feedback") {
      openFeedbackModal();
      return;
    }
    // Code Triage opens the code agent with session context.
    if (action === "code_triage") {
      // Never from the customer's page: the bar hides the button there, and
      // this keeps any other caller from opening the code agent (and copying
      // the transcript into sessionStorage) on the enterprise surface.
      if (variant === "enterprise") return;
      // Build a summary of the session for code triage
      const contextLines = [];
      contextLines.push("## Session Summary (from Copilot)\n");
      if (callContextBlocksRef.current.length > 0) {
        contextLines.push("**Call Context:**");
        callContextBlocksRef.current.forEach(b => {
          if (b.text) contextLines.push(b.text);
        });
        contextLines.push("");
      }
      contextLines.push("**Conversation:**");
      messages.forEach(m => {
        const who = m.sender === "user" ? "Engineer" : "Copilot";
        const text = (m.text || "").slice(0, 500);
        if (text.trim()) contextLines.push(`- ${who}: ${text}`);
      });
      const context = contextLines.join("\n");
      // Store in sessionStorage for the CodeAgentPanel to pick up
      const targetSessionId = sessionIdRef.current || "new";
      sessionStorage.setItem(`code-agent-context-${targetSessionId}`, context);
      // Open code triage in new tab with session reference
      window.open(`/code-agent/${targetSessionId}`, "_blank");
      return;
    }
    // Everything below drives an agent turn, which a timed-out id cannot serve.
    if (sessionExpired) return;
    const echo = ACTION_ECHO[action];
    if (echo) {
      lockSourceReplacement();
      appendMessage({ sender: "agent", text: echo });
      pendingQuestionSnapshotRef.current = null;
      setCurrentQuestion(null);
      setIsStreaming(true);
      turnStartRef.current = Date.now();
      turnPendingRef.current = true;
    }
    // A turn is a turn wherever it came from. The gate above normally means a
    // question was already typed (so the session exists), but attached call
    // context satisfies it too — and that path can open a chat from /new
    // without the composer ever being used.
    withSession(() => sendAction(action));
  }, [sendAction, appendMessage, openFeedbackModal, sessionExpired, withSession, variant, lockSourceReplacement]);

  const handleShareToCustomer = useCallback((text) => {
    // Citations are internal. The customer has no source cards on screen, so
    // an [S2] pill refers to nothing they can see, and the unverified marker is
    // a QA signal meant for the engineer. Stripped at the one choke point every
    // "Send to customer" passes through, rather than at each call site.
    customerPanelRef.current?.sendToCustomer(stripCitations(text));
  }, []);

  // V5: the Diagnostic Agent's diagnosis is open (never on enterprise, where the popover is hidden too).
  const diagnosisActive = variant !== "enterprise" && diagnosisOpen(brain.diagState);

  const handleExitGuided = useCallback(() => {
    if (sessionExpired || (copilotMode !== "guided" && !diagnosisActive)) return;
    pendingQuestionSnapshotRef.current = null;
    setCurrentQuestion(null);
    setIsStreaming(false);
    setCopilotMode("fast");
    exitGuided();
  }, [exitGuided, sessionExpired, copilotMode, diagnosisActive]);

  // Correcting the triaged product re-runs the first prompt server-side under
  // the same session id. The click only ASKS: nothing on screen changes except
  // the control, which goes pending. The answer the engineer is reading keeps
  // streaming into its own bubble, because the server may still refuse (the
  // old run did not stop in time, the session moved past its first prompt) —
  // and a wipe done here could not be undone. The wipe is
  // onProductChangeAccepted's, on the server's word.
  const handleChangeProduct = useCallback((msgId, product) => {
    if (!product || sessionExpired || sessionClosed || productLocked) return;
    if (productChangePendingRef.current) return;
    if (product === productRef.current) return;
    setChangePending(product);
    changeProduct(product);
  }, [changeProduct, sessionExpired, sessionClosed, productLocked, setChangePending]);

  const handleEditInCustomer = useCallback((text) => {
    customerPanelRef.current?.fillComposer(text);
  }, []);

  // The left panel's send button: attach the call's notes to this session as
  // CONTEXT. Nothing is asked and nothing is generated — the agent simply
  // knows the call now, and the engineer's next question (or Find Cases /
  // Find Bugs) runs against it.
  const pushCallContext = useCallback(() => {
    const joined = callContextBlocksRef.current.map((b) => b.text).filter(Boolean).join("\n\n");
    // A no-op with no session (attachCallContext has nothing to address), which
    // is fine: the blocks live in local state and the WHOLE concatenation is
    // re-sent on every push, so onSessionCreated replaying this once loses
    // nothing that was attached while the chat was still blank.
    attachCallContext(joined);
  }, [attachCallContext]);
  pushCallContextRef.current = pushCallContext;

  // The left panel's send button. Adds one context block to the thread and
  // tells the agent about it. By default NOTHING runs: no turn, no search, no
  // answer -- the engineer types when they're ready, and that message is what
  // carries the context in (see _with_call_context server-side).
  //
  // With Root Cause Analysis on, the same click also asks for the analysis.
  // Note what is NOT sent: the context itself. The server already attached it
  // and prefixes it onto every turn, so repeating it in the message would put
  // the same facts in front of the model twice. The message is the ask; the
  // context is context.
  //
  // Each block is stamped with the wall-clock time it was attached, as a first
  // line of the block's own text rather than as metadata: the stamp then rides
  // along through the join, the edits and the full-context merge with no extra
  // plumbing, and the agent reads the ordering of the call the same way the
  // engineer does. 24h local time — the engineer and the caller are on it.
  const handleRequestDiagnosis = useCallback((factsText) => {
    const raw = String(factsText || "").trim();
    if (!raw) return;
    const stamp = new Date().toLocaleTimeString([], { hour12: false });
    const text = `[${stamp}]\n${raw}`;
    const id = `ctx-${Date.now()}`;
    setCallContextBlocks((prev) => [...prev, { id, text }]);
    appendMessage({ id, type: "call_context", text });
    pushCallContext();
    // After pushCallContext, and on the same socket, so tse:set_call_context
    // is handled before the turn it feeds.
    if (autoRca) handleSend(RCA_PROMPT);
  }, [appendMessage, pushCallContext, autoRca, handleSend, setCallContextBlocks]);

  // Engineer corrected a block in the thread — live transcription mangles the
  // words that matter most. Their version replaces it from the next turn on.
  const handleCallContextEdit = useCallback((id, text) => {
    setCallContextBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, text } : b)));
    patchMessage(id, { text });
    pushCallContext();
  }, [patchMessage, pushCallContext, setCallContextBlocks]);

  // "Full context" save in GlobalContextPanel: the engineer rewrote the whole
  // thing, so the block boundaries they rewrote across no longer exist. The
  // list collapses to one block carrying their text — kept under the FIRST
  // block's id so the thread reads in place — and the later cards stay in the
  // thread marked superseded: still a record of what the agent was told then,
  // no longer part of what it is told now. New call notes attach after it.
  const handleCallContextReplaceAll = useCallback((text) => {
    const next = String(text || "").trim();
    if (!next) return;
    const blocks = callContextBlocksRef.current;
    const keepId = blocks[0]?.id || `ctx-${Date.now()}`;
    if (blocks.length === 0) {
      appendMessage({ id: keepId, type: "call_context", text: next });
    } else {
      patchMessage(keepId, { text: next });
      blocks.slice(1).forEach((b) => patchMessage(b.id, { superseded: true }));
    }
    setCallContextBlocks([{ id: keepId, text: next }]);
    pushCallContext();
  }, [appendMessage, patchMessage, pushCallContext, setCallContextBlocks]);

  const handleDocError = useCallback((message) => {
    appendMessage({ type: "error", text: message });
  }, [appendMessage]);

  const handleStop = useCallback(() => {
    stop();
    commandBusy.release();
    setIsStreaming(false);
    streamingMsgIdRef.current = null;
  }, [stop, commandBusy]);

  // Open the diagnostic map scoped to a single source document in a new tab.
  // Uses localStorage as a fast handoff for the snapshot data, with URL params
  // as fallback so the page can re-fetch if localStorage is cleared or quota-full.
  // The key and URL are product-keyed (session-independent) because
  // isolateSnapshot strips every session-dependent field — the same product +
  // file always yields the same rendered graph.
  const handleOpenSourceMap = useCallback((src) => {
    // Build the target list: aggregate cards carry file_names (multi-runbook),
    // single-doc cards have file_name, title is the last resort fallback.
    const names = (Array.isArray(src?.file_names) && src.file_names.length)
      ? src.file_names
      : [src?.file_name || src?.title || ""].filter(Boolean);
    const srcTitle = src?.title || "Diagnostic Map";
    // The ref is authoritative — it is set from both the live tse:info path and
    // the resume path. The transcript scan stays as a fallback for a session
    // hydrated purely from replayed history.
    const product =
      productRef.current ||
      messages.find((m) => m.type === "product" && m.product)?.product ||
      "";

    // Deterministic key so re-clicking the same source reuses one entry.
    // Product-keyed (v2): same product + file = same cache slot regardless of
    // which session opened it. Prefer card_key (stable across sessions for
    // aggregate cards), then the first file name, then the title.
    const slugSrc = src?.card_key || names[0] || srcTitle || "map";
    const slug = slugifySourceKey(slugSrc);
    const lsKey = sourceMapKey(product, slug);

    // V5 (D12): the main app no longer holds the old map's product graph, so no
    // pre-scoped snapshot is handed over. A stale entry from an earlier click is
    // removed and the /source-map page fetches the graph from its URL params.
    try {
      localStorage.removeItem(lsKey);
    } catch (_) {
      // Storage blocked — the page fetches via URL params either way.
    }

    // Session-free URL: /source-map?product=...&file=...
    // buildSourceMapUrl shares its slug rules with the page.
    window.open(
      buildSourceMapUrl({ slug, names, product, title: srcTitle }),
      "_blank",
      "noopener"
    );
  }, [messages]);

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev) => {
      if (!isResizingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      const newPct = Math.min(Math.max(pct, 20), 80);
      setLeftPct(newPct);
      setLastLeftPct(newPct);
    };
    const onUp = () => {
      isResizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  useEffect(() => () => {
    streamingMsgIdRef.current = null;
    if (feedbackNoticeTimerRef.current) window.clearTimeout(feedbackNoticeTimerRef.current);
    if (actionNoticeTimerRef.current) window.clearTimeout(actionNoticeTimerRef.current);
    if (startTimeoutRef.current) window.clearTimeout(startTimeoutRef.current);
    // The clear gate's safety valve, so it cannot fire into a dead tree.
    if (clearInFlightTimerRef.current) clearTimeout(clearInFlightTimerRef.current);
    clearInFlightRef.current = false;
  }, []);

  return (
    <div className={`d5-root d5-variant-${variant} ${embedded ? "d5-root-embedded" : ""}`}>

      {/* Access-denied toast. Rendered at the root and fixed-position so it is
          not clipped by the split panes, and role="alert" so the redirect is
          announced — the user asked for a session and silently landed in a
          different, empty one. */}
      {accessDeniedToast && (
        <div className="d5-access-denied-toast" role="alert">
          <span className="d5-access-denied-toast__title">Access Denied</span>
          <span className="d5-access-denied-toast__body">
            That chat belongs to another user. Started a new session instead.
          </span>
        </div>
      )}
      {agentJoinedNotice && (() => {
        const isEnded = /ended|closed|disconnected/i.test(agentJoinedNotice);
        return (
          <div
            role="status"
            style={{
              position: "fixed",
              top: "1.25rem",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 2000,
              background: "linear-gradient(135deg, #0ea5e9, #2563eb)",
              color: "#fff",
              borderRadius: "0.75rem",
              padding: "0.65rem 1.25rem",
              fontSize: "0.8rem",
              fontWeight: 600,
              boxShadow: "0 4px 20px rgba(14,165,233,0.35)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <span>{isEnded ? "🔴" : "🟢"}</span> {agentJoinedNotice}
          </div>
        );
      })()}
      {!embedded && <SideBar mode="agent" onLogout={onLogout} isDarkMode={false} onToggleTheme={() => {}} onLinkClick={(path) => navigate(path)} currentPath="/TseDashboard5" />}

      <div className="d5-main">
        <div ref={splitContainerRef} className="d5-split">
          {!embedded && (
            <>
              <div
                className="d5-split-left"
                style={{
                  width: `${leftPct}%`,
                  display: leftPct === 0 ? "none" : "block"
                }}
              >
                <CustomerChatPanel
                  ref={customerPanelRef}
                  embedded={embedded}
                  socket={socket}
                  isConnected={isConnected}
                  agentName={agentName}
                  aiAgentName={customerAiAgentName || (variant === "enterprise" ? agentNameOverride : "") || "AI Agent"}
                  customerUsername={customerUsername}
                  liveChatEnded={liveChatEnded}
                  liveChatEndedBy={liveChatEndedBy}
                  onEndLiveChat={handleEndLiveChat}
                  onCollapse={() => setLeftPct(0)}
                  enableZoomBridge={!escalationActive}
                  /* The CUSTOMER's id, not this dashboard's. It used to be
                     sessionIdRef, which addressed the engineer's own session —
                     so every live-chat message was sent to the engineer. */
                  session={customerSessionIdRef.current && (escalationActive || liveChatEnded)
                    ? {
                        user_sid: customerSessionIdRef.current,
                        escalationActive: escalationActive && !liveChatEnded,
                        liveChatEnded,
                      }
                    : undefined}
                  customerSessionId={customerSessionIdRef.current || ""}
                  sessionId={sessionIdRef.current || sessionId || ""}
                  onRequestDiagnosis={handleRequestDiagnosis}
                  onJoinCall={handleJoinCall}
                  contextCount={callContextBlocks.length}
                  contextChars={callContextChars}
                  contextOpen={contextPanelOpen}
                  onToggleContext={() => setContextPanelOpen((v) => !v)}
                  /* Same two numbers the profile menu shows, so the summary the
                     engineer reads on taking over carries the customer's mood
                     with it instead of making them open a second surface. */
                  sentiment={sentiment}
                  prevSentiment={prevSentiment}
                />
                {/* Overlays the left pane, not a modal: the context belongs to
                    the call, so it opens over the call and leaves the Copilot
                    thread readable beside it. */}
                <GlobalContextPanel
                  open={contextPanelOpen}
                  blocks={callContextBlocks}
                  onClose={() => setContextPanelOpen(false)}
                  onReplaceAll={handleCallContextReplaceAll}
                />
              </div>
              {leftPct > 0 && (
                <div onMouseDown={handleResizeMouseDown} className="d5-resize-handle" title="Drag to resize" />
              )}
            </>
          )}

          <div
            className="d5-split-right"
            style={{ width: embedded ? "100%" : `${100 - leftPct}%`, display: "flex", minWidth: 0 }}
          >
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <CopilotPanel
              ref={copilotPanelRef}
              onCallContextEdit={handleCallContextEdit}
              onEndLiveChat={handleEndLiveChat}
              rcaEnabled={autoRca}
              onToggleRca={() => setAutoRca((v) => !v)}
              messages={messages}
              currentQuestion={currentQuestion}
              isStreaming={isStreaming}
              narrationPending={narration.awaiting}
              sessionStats={sessionStats}
              onSend={handleSend}
              onAction={handleAction}
              onCaseAnalysisSubmit={handleCaseAnalysisSubmit}
              onAnswer={handleAnswer}
              onDismissQuestion={() => { pendingQuestionSnapshotRef.current = null; setCurrentQuestion(null); }}
              onStop={handleStop}
              onShareToCustomer={handleShareToCustomer}
              onEditInCustomer={handleEditInCustomer}
              onOpenFeedback={openFeedbackModal}
              onOpenLearning={() => setLearningOpen(true)}
              onResumeContextSwitch={handleResumeContextSwitch}
              feedbackNotice={feedbackNotice}
              actionNotice={actionNotice}
              onLogout={onLogout}
              onDocError={handleDocError}
              /* Engineers only. /source-map is gated on AGENT_ROLES (App.js), so
                 a customer clicking this got Navigate to "/" — which, for role
                 "user", lands on /ent-user: the button threw them out of their
                 own chat. Withheld rather than gated inside SourceCards: no
                 handler is already how that component decides there is nowhere
                 to open a map, so the button simply is not drawn. Same call as
                 Find Cases / Find Bugs / Active Incident in ActionBar. */
              onOpenSourceMap={variant === "enterprise" ? undefined : handleOpenSourceMap}
              onReplaceSource={handleReplaceSource}
              onCiteSource={handleCiteSource}
              bugs={sidebar.bugs}
              cases={sidebar.cases}
              resultsMeta={resultsMeta}
              variant={variant}
              agentNameOverride={agentNameOverride}
              sentiment={sentiment}
              prevSentiment={prevSentiment}
              escalationActive={escalationActive}
              liveChatEnded={liveChatEnded}
              customerUsername={customerUsername}
              tseAgentName={liveAgentName}
              copilotLabel={variant === "enterprise" ? (agentNameOverride || "AI Agent") : "Copilot"}
              sessionExpired={sessionExpired}
              sessionClosed={sessionClosed}
              reconnecting={reconnecting}
              sessionEndReason={sessionEndReason}
              onNewSession={handleClear}
              onResumeSession={handleResumeSession}
              resumePending={resumePending}
              copilotMode={copilotMode}
              onExitGuided={handleExitGuided}
              diagnosisActive={diagnosisActive}
              productLocked={productLocked}
              productChangePending={productChangePending}
              onChangeProduct={variant === "enterprise" ? undefined : handleChangeProduct}
              isCollapsed={leftPct === 0}
              onExpand={() => setLeftPct(lastLeftPct)}
              sessionId={sessionIdRef.current || ""}
              ctxTurnKey={ctxTurnKey}
              ctxResetKey={ctxResetKey}
              hydrating={hydrating}
              hydratingTitle={hydratingTitle}
              sessionSource={sessionSource}
              hydrateFailed={hydrateFailed}
              onRetryHydrate={handleRetryHydrate}
              onDiagnosisPrompt={handleDiagnosisPrompt}
              onLearningPromptOpen={handleLearningPromptOpen}
              onResultsOpenChange={setResultsOverlayOpen}
            />
            </div>
          </div>
        </div>
      </div>

      {showFeedbackModal && (
        <FeedbackModal
          sessionId={sessionIdRef.current || ""}
          username={agentName}
          firstPrompt={firstPromptRef.current}
          surface={variant === "enterprise" ? "user" : "engineer"}
          onClose={() => setShowFeedbackModal(false)}
        />
      )}

      {learningOpen && (
        <LearningMode
          sessionId={sessionIdRef.current || ""}
          messages={messages}
          onClose={() => setLearningOpen(false)}
        />
      )}

      {showZoomModal && (
        <ZoomLinkModal
          onConfirm={handleZoomLinkConfirm}
          onCancel={() => setShowZoomModal(false)}
        />
      )}

      {/* V5 diagnosis popover (plan 15.4). Hidden until the first tse:diag_state
          of the session; never on the enterprise variant (O9, 16.1). */}
      <BrainPopover
        diagState={popoverDiagState}
        sessionId={sessionId || ""}
        username={loginUsername || agentName || ""}
        enterprise={variant === "enterprise"}
        overlayOpen={resultsOverlayOpen}
        devMode={readStoredRole() === BRAIN_DEV_ROLE}
        fetcher={authFetch}
        onCommand={handleBrainCommand}
        onFocusComposer={focusComposer}
        openRequest={brainOpenRequest}
        onExit={handleExitGuided}
        learning={variant === "enterprise" ? null : {
          enabled: learningEnabled, pending: learningPending, busy: isStreaming, hasSession: !!sessionId,
          onToggle: handleToggleLearning,
        }}
      />
    </div>
  );
};

export default TseDashboard5;
